<template>
  <div class="create-booking-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="onBackClick">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <div class="view-title-group">
        <h5 class="view-title mb-0">{{ viewHeaderTitle }}</h5>
        <div v-if="isAppendMode && appendContextLabel" class="view-subtitle">{{ appendContextLabel }}</div>
      </div>
    </div>

    <!-- The form stays mounted (v-show) while the item picker is open so the
         Stripe / Square card elements and typed customer details survive. -->
    <div v-show="!itemPickerOpen" class="view-body">
      <!-- Tickets section -->
      <drawer-section :title="ticketsSectionTitle" :icon="ticketsSectionIcon">

        <!-- Cart-mode: one CartLine per slot in the cart. Each line owns
             its own pricing fetch + category steppers + upgrade-to-private
             toggle and emits its line subtotal up here. Aggregate
             Subtotal/tax/fee/total below this block reads from the sum. -->
        <template v-if="isCartMode">
          <cart-line
            v-for="entry in cartSlots"
            :key="'cart-' + entry.id"
            :slot-entry="entry"
            :company-id="companyId"
            :company-group-id="companyGroupId"
            :date-format="date_format"
            :time-format="time_format"
            :currency="currency"
            :currency-symbol="currency_symbol"
            :currency-format="currency_format"
            :number-format="number_format"
            @update="onCartLineUpdate"
            @remove="onCartLineRemove"
          />
        </template>

        <!-- Single-slot mode: existing inline upgrade-toggle + pricing-row
             v-for + per-slot subtotal. Untouched logic — just gated. -->
        <template v-if="!isCartMode">
        <!-- Upgrade to private reservation toggle (mirrors Bookings.vue:925) -->
        <div v-if="canUpgradeToPrivate" class="upgrade-toggle">
          <div class="upgrade-toggle__row">
            <el-switch
              active-color="#e14eca"
              v-model="upgradeToPrivate"
              @change="recalculateTotal()"
            />
            <label class="upgrade-toggle__label text-white">Upgrade to private reservation</label>
          </div>
          <p class="upgrade-toggle__desc">
            Books the entire time slot exclusively for this customer.
            <template v-if="upgradePricingMode === 'tickets_plus_fee'">
              Charged as per ticket pricing plus a <strong>{{ getAmount(upgradeFee) }}</strong> private booking fee.
            </template>
            <template v-else>
              A flat <strong>{{ getAmount(upgradeFee) }}</strong> buyout fee replaces per ticket pricing.
            </template>
          </p>
        </div>

        <!-- Multiple durations: pick the length before the ticket counts -->
        <div v-if="showDurationPicker" class="mb-3">
          <label class="text-white d-block mb-1">Duration</label>
          <segmented-toggle variant="pills" size="sm" v-model="selectedDurationId" :options="durationOptions" @input="onDurationChange" />
        </div>

        <div
          v-for="(c, i) in pricingCategories"
          :key="'C' + i"
          class="pricing-row"
        >
          <div class="pricing-row__info">
            <div class="pricing-row__name-line">
              <span class="pricing-row__name text-white">{{ c.category }}</span>
              <template v-if="!c.editing">
                <el-tooltip content="Override price" placement="top" :open-delay="300">
                  <i class="tim-icons icon-pencil pricing-row__edit-icon" @click="startEditPrice(c)"></i>
                </el-tooltip>
              </template>
            </div>
            <template v-if="!c.editing">
              <span class="pricing-row__rate">{{ getRate(c) }}</span>
            </template>
            <template v-else>
              <div class="pricing-row__inline-edit">
                <base-input class="mb-0 pricing-row__price-input">
                  <el-input-number
                    v-model="c.price"
                    :min="0"
                    :precision="2"
                    :step="0.01"
                    :controls="false"
                    size="mini"
                    @change="recalculateTotal()"
                  />
                </base-input>
                <el-tooltip content="Save" placement="top" :open-delay="300">
                  <i class="tim-icons icon-check-2 pricing-row__save-icon" @click="saveEditPrice(c)"></i>
                </el-tooltip>
                <el-tooltip content="Restore original price" placement="top" :open-delay="300">
                  <i class="tim-icons icon-refresh-02 pricing-row__restore-icon" @click="restoreOriginalPrice(c)"></i>
                </el-tooltip>
                <el-tooltip content="Cancel" placement="top" :open-delay="300">
                  <i class="tim-icons icon-simple-remove pricing-row__cancel-icon" @click="cancelEditPrice(c)"></i>
                </el-tooltip>
              </div>
            </template>
          </div>
          <div class="pricing-row__qty">
            <base-input class="mb-0">
              <el-input-number
                v-model="c.number_of_players"
                :min="0"
                size="mini"
                @change="recalculateTotal()"
              />
            </base-input>
          </div>
          <div v-if="!upgradeToPrivate || upgradePricingMode === 'tickets_plus_fee'" class="pricing-row__total text-white">
            {{ getAmount(getLineTotal(c)) }}
          </div>
        </div>
        </template>
        <!-- End single-slot Tickets UI; aggregate Subtotal/tax/fee/total
             below serves BOTH cart mode and single-slot mode by reading
             this.subtotal which we set differently per mode in
             recalculateTotal. -->

        <!-- Private booking fee (tickets+fee upgrade): part of the pre-tax
             subtotal, shown as a component so the ticket lines + fee reconcile to it. -->
        <div v-if="upgradeToPrivate && upgradePricingMode === 'tickets_plus_fee'" class="summary-line">
          <span class="text-white">Private booking fee</span>
          <span class="text-white">{{ getAmount(upgradeFee) }}</span>
        </div>

        <!-- Receipt styling shared with the booked slot drawer's Transaction
             summary (global _receipt.scss). The ticket rows above are the
             booking; staged items list here, then the totals they make up. -->
        <div class="receipt">
          <div v-if="stagedItems.length > 0" class="receipt-items">
            <div v-for="line in stagedItems" :key="line.key" class="receipt-item">
              <div class="receipt-item__desc">
                <div class="receipt-item__name-row">
                  <span class="text-white">{{ line.name }}</span>
                  <span class="receipt-item__qty text-muted">x{{ line.quantity }}</span>
                  <button type="button" class="receipt-row__delete" :aria-label="`Remove ${line.name}`" @click="removeStagedItem(line.key)">
                    <i class="tim-icons icon-trash-simple"></i>
                  </button>
                </div>
                <div class="receipt-item__meta">{{ getAmount(line.price) }} each</div>
              </div>
              <span class="text-white">{{ getAmount(line.amount) }}</span>
            </div>
          </div>
          <div class="receipt-divider"></div>

          <div class="receipt-totals">
            <div class="receipt-row">
              <span class="text-white">Subtotal</span>
              <span class="text-white">{{ getAmount(subtotal) }}</span>
            </div>

            <!-- Promos, taxes, fees, total, and waive toggles are all transaction-
                 level concerns. In append mode they come from the existing
                 transaction's stored state and get recomputed server-side by
                 update-transaction-values after the append, so showing FE-local
                 calculations here would mislead (e.g., waive flags can't be sent
                 via add-booking-to-transaction). Hide the whole breakdown — the
                 user just sees what they're contributing (subtotal). Banner
                 updates with the authoritative total after submit. -->
            <template v-if="!isAppendMode">
              <div class="receipt-row" v-for="(p, i) in promos" :key="'PC' + i" v-show="subtotal > 0">
                <span class="receipt-row__name-group text-white">
                  <span>{{ p.name }}</span>
                  <button type="button" class="receipt-row__delete" :aria-label="`Remove ${p.name}`" @click="removePromoCode(p)">
                    <i class="tim-icons icon-trash-simple"></i>
                  </button>
                </span>
                <span class="text-danger">-{{ getAmount(getDiscountAmount(p)) }}</span>
              </div>

              <div class="receipt-row" v-for="(entry, i) in taxBreakdown" :key="'T' + i" v-show="!waiveTax && subtotal > 0 && entry.amount > 0">
                <span class="text-white">{{ entry.tax.name }}<span v-if="entry.tax.pricing_type === 1" class="receipt-row__link"> (included in subtotal)</span></span>
                <span class="text-white">{{ getAmount(entry.amount) }}</span>
              </div>

              <div class="receipt-row" v-for="(f, i) in nonOptionalFees" :key="'F' + i" v-show="!waiveFee && getFeeAmount(f) > 0">
                <span class="text-white">{{ f.name }}</span>
                <span class="text-white">{{ getAmount(getFeeAmount(f)) }}</span>
              </div>
            </template>
          </div>

          <template v-if="!isAppendMode">
            <div class="receipt-adjustments">
              <a href="#" class="receipt-adjustments__add" @click.prevent="itemPickerOpen = true">
                <i class="tim-icons icon-cart"></i> Add item
              </a>
            </div>
            <div class="receipt-divider"></div>
            <div class="receipt-totals">
              <div class="receipt-row receipt-row--bold">
                <span class="text-white">Total</span>
                <span class="text-white">{{ getAmount(total) }}</span>
              </div>
            </div>
          </template>
        </div>

        <template v-if="!isAppendMode">
          <!-- Waive options (folded into Tickets) -->
          <div class="waive-row">
            <el-switch v-model="waiveTax" @change="recalculateTotal()" active-color="#e14eca" />
            <span class="ml-2 text-white">Waive taxes</span>
            <el-switch v-model="waiveFee" @change="recalculateTotal()" active-color="#e14eca" class="ml-4" />
            <span class="ml-2 text-white">Waive fees</span>
          </div>
        </template>
      </drawer-section>

      <!-- Promo code (transaction-level — hidden in append mode) -->
      <drawer-section v-if="!isAppendMode" title="Promo code" icon="icon-tag">
        <base-input class="mb-0">
          <el-autocomplete
            style="width: 100%"
            v-model="promoCode"
            :fetch-suggestions="promoSearch"
            placeholder="Search promo..."
            :trigger-on-focus="false"
            @select="handlePromoSelect"
          >
            <template #default="{ item }">
              <div>
                <span class="text-primary">{{ item.name }}</span>
                <span v-if="!hasBuyXGetYRule(item.rules)"> - </span>
                <small>
                  <template v-if="!hasBuyXGetYRule(item.rules)">
                    <span v-if="item.redemption_type === 0">{{ item.value_amount }}%</span>
                    <span v-if="item.redemption_type === 1">{{ getAmount(item.value_amount) }}</span>
                    <span v-if="item.value_type === 0"> per transaction</span>
                    <span v-if="item.value_type === 1"> per line item</span>
                    <span v-if="item.value_type === 2"> per quantity</span>
                  </template>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #1d8cf8; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasBuyXGetYRule(item.rules)"
                  >buy x get y</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #344675; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="item.internal_only"
                  >internal only</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #00bf9a; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasMinValueRule(item.rules)"
                  >value rule</span>
                  <span
                    class="ml-2"
                    style="text-transform: uppercase; line-height: 9px; color: white; background-color: #ff8d72; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                    v-if="hasBundlePriceRule(item.rules)"
                  >bundle price</span>
                </small>
              </div>
            </template>
            <template #append>
              <button class="apply-promo-append" type="button" @click="applyPromoCode">Apply</button>
            </template>
          </el-autocomplete>
        </base-input>
      </drawer-section>

      <!-- Customer details (transaction-level — hidden in append mode) -->
      <drawer-section v-if="!isAppendMode" title="Customer details" icon="icon-single-02">
        <info-hint v-if="assigningRequest" icon="icon-paper" variant="primary" class="mb-2">Booking for request #{{ assigningRequest.id }}. Completing this booking approves the request and links it to the order.</info-hint>
        <base-input label="Customer lookup">
          <el-autocomplete
            style="width: 100%"
            v-model="lookupValue"
            :fetch-suggestions="querySearchCustomer"
            placeholder="Search by name, email, or phone"
            :trigger-on-focus="false"
            @select="handleSelectCustomer"
          >
            <template #default="{ item }">
              <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
              <small class="font-italic">
                <i class="tim-icons icon-email-85"></i> {{ item.email }}
                <span v-if="item.phone">
                  <i class="tim-icons el-icon-mobile-phone ml-2"></i> {{ item.phone }}
                </span>
              </small>
            </template>
            <template #append>
              <button class="lookup-clear-append" type="button" @click="clearCustomer">
                <i class="tim-icons icon-refresh-02"></i>
              </button>
            </template>
          </el-autocomplete>
        </base-input>

        <div class="form-row">
          <div class="form-field form-field--half">
            <base-input :error="errs.first_name" label="First name *" v-model="user.first_name" placeholder="First name" />
          </div>
          <div class="form-field form-field--half">
            <base-input :error="errs.last_name" label="Last name *" v-model="user.last_name" placeholder="Last name" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-field form-field--half">
            <base-input :error="errs.email" label="Email *" v-model="user.email" placeholder="Email" />
          </div>
          <div class="form-field form-field--half">
            <base-input
              type="text"
              :label="isPhoneRequired() ? 'Phone *' : 'Phone (optional)'"
              :error="errs.phone"
            >
              <phone-number-input
                ref="phoneInput"
                v-model="user.phone"
                @phone-update="handlePhoneUpdate"
                :default-country="countryCode || 'US'"
              />
            </base-input>
          </div>
        </div>
      </drawer-section>

      <!-- Additional info (internal custom fields — hidden in append mode) -->
      <drawer-section v-if="!isAppendMode && (loadingCustomFields || visibleCustomFields.length > 0)" title="Additional info" icon="icon-paper">
        <custom-field-response-renderer
          ref="cfRenderer"
          :fields="visibleCustomFields"
          v-model="customFieldResponses"
          :disabled="loading"
          :loading="loadingCustomFields"
        />
      </drawer-section>

      <!-- Payment options (transaction-level — append routes through CollectPaymentView later) -->
      <drawer-section v-if="!isAppendMode" title="Payment options" icon="icon-credit-card">
        <div class="email-option-row">
          <el-switch active-color="#e14eca" v-model="addPayment" @input="onPaymentToggle" />
          <label class="ml-2 text-white">Take payment now</label>
        </div>

        <template v-if="addPayment">
          <div v-if="depositRequired" class="payment-deposit-hint">
            <info-hint variant="primary" icon="icon-coins">
              Deposit required for this booking: <strong>{{ getAmount(depositChargeAmount) }}</strong>. Pay in full or enter a different amount below.
            </info-hint>
          </div>
          <div v-if="depositApplicable" class="payment-mode-row">
            <segmented-toggle
              size="sm"
              :value="paymentMode"
              :options="paymentModeOptions"
              @input="onPaymentModeChange"
            />
          </div>
          <div class="form-row">
            <div class="form-field form-field--narrow">
              <label class="field-label">Amount to pay</label>
              <div class="amount-input-group" :class="{ 'has-error': errs.partial_amount, 'is-locked': depositApplicable && paymentMode !== 'custom' }">
                <span class="amount-prefix">{{ actualCurrencySymbol }}</span>
                <base-input class="amount-input-wrapper mb-0" :error="errs.partial_amount">
                  <el-input-number
                    placeholder="0.00"
                    v-model="partialAmount"
                    :controls="false"
                    :precision="2"
                    :min="0"
                    :max="canExceedTotal ? Infinity : total"
                    :disabled="depositApplicable && paymentMode !== 'custom'"
                    @change="validatePartialAmount"
                  />
                </base-input>
              </div>
            </div>
            <div class="form-field form-field--half" v-if="selectedPaymentOption.name === 'Customer credit'">
              <label class="field-label">Available credit</label>
              <div class="amount-input-group available-credit">
                <span class="amount-prefix">{{ actualCurrencySymbol }}</span>
                <base-input class="amount-input-wrapper mb-0">
                  <el-input-number
                    :value="availableCredit"
                    :controls="false"
                    :precision="2"
                    disabled
                  />
                </base-input>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field form-field--half">
              <label class="field-label">Payment method</label>
              <el-select
                class="select-primary w-100"
                v-model="selectedPaymentOption"
                placeholder="Select payment method"
                value-key="id"
              >
                <el-option
                  class="select-primary"
                  v-for="(item, index) in paymentOptions"
                  :key="index + 'P'"
                  :label="item.name"
                  :value="{id: item.id, name: item.name, content: item.content, is_default: item.is_default}"
                />
              </el-select>
            </div>
            <!-- Stored cards dropdown (Stripe) -->
            <div class="form-field form-field--half" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 3 && paymentSettings.store_credit_card && stripePaymentMethods.length > 0">
              <label class="field-label">Stored cards</label>
              <el-select
                class="select-primary w-100"
                v-model="selectedPaymentMethod"
                placeholder="Select card"
                @change="onPaymentMethodChange"
              >
                <el-option class="select-primary" label="Use a new card" value="new"></el-option>
                <el-option
                  class="select-primary"
                  v-for="pm in stripePaymentMethods"
                  :key="pm.id"
                  :label="getCardBrandName(pm.card_brand) + ' ****' + pm.card_last_four + (pm.is_default ? ' (Default)' : '')"
                  :value="pm.id"
                />
              </el-select>
            </div>
          </div>

          <!-- Gift card -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Gift card'">
            <base-input label="Gift card code" :error="errs.gift_card_code">
              <el-autocomplete
                style="width: 100%"
                v-model="giftCardCode"
                :fetch-suggestions="giftCardSearch"
                placeholder="Search by code..."
                :trigger-on-focus="false"
                @select="handleGiftCardSelect"
              >
                <template #default="{ item }">
                  <p style="margin-bottom: -12px" class="text-primary">{{ getGiftCardCode(item.code) }}</p>
                  <small class="font-italic">
                    <i class="tim-icons icon-money-coins"></i> Total {{ getAmount(item.total) }} Remaining {{ getAmount(item.remaining) }}
                  </small>
                </template>
              </el-autocomplete>
            </base-input>
          </div>

          <!-- Stripe stored card display -->
          <div class="stored-card-display" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 3 && selectedPaymentMethod !== 'new' && selectedStoredCard">
            <div class="d-flex align-items-center">
              <i :class="getCardBrandIcon(selectedStoredCard.card_brand)" class="stored-card-icon mr-3"></i>
              <div>
                <p class="mb-0 text-white">{{ getCardBrandName(selectedStoredCard.card_brand) }} ****{{ selectedStoredCard.card_last_four }}</p>
                <small style="color: rgba(255,255,255,0.5);" v-if="selectedStoredCard.cardholder_name">{{ selectedStoredCard.cardholder_name }}</small>
                <small style="color: rgba(255,255,255,0.5);"> &middot; Expires {{ selectedStoredCard.card_exp_month }}/{{ selectedStoredCard.card_exp_year }}</small>
              </div>
            </div>
          </div>

          <!-- Stripe new card form -->
          <div class="form-field" v-show="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 3 && (selectedPaymentMethod === 'new' || stripePaymentMethods.length === 0)">
            <form id="payment-form-drawer-booking">
              <label for="card-element-drawer-booking">Credit or debit card</label>
              <div id="card-element-drawer-booking"></div>
              <div id="card-errors-drawer-booking" role="alert"></div>
            </form>
          </div>

          <!-- Save card (Stripe new card) -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 3 && paymentSettings.store_credit_card && (selectedPaymentMethod === 'new' || stripePaymentMethods.length === 0) && (user.end_user_id || user.email)">
            <el-switch active-color="#e14eca" v-model="saveCardForFuture"></el-switch>
            <label class="ml-2 text-white">Save this card for future payments</label>
          </div>

          <!-- Square card form -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 2">
            <form id="payment-form-square-drawer-booking">
              <div id="card-container-drawer-booking"></div>
            </form>
          </div>

          <!-- Save card (Square) -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 2 && paymentSettings.square_store_credit_card && (user.end_user_id || user.email)">
            <el-switch active-color="#e14eca" v-model="saveCardForFuture"></el-switch>
            <label class="ml-2 text-white">Save this card for future payments</label>
          </div>

          <!-- Authorize.net -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 1">
            <enhanced-payment-form
              ref="authorizePaymentForm"
              :api-login-id="paymentSettings.api_name"
              :public-client-key="paymentSettings.public_client_key"
              :is-live="!!paymentSettings.is_live"
              :show-security-indicators="false"
            />
          </div>

          <!-- Paystack info -->
          <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && paymentSettings.payment_gateway_id === 4">
            <div class="d-flex align-items-center" style="padding: 8px 0;">
              <i class="tim-icons icon-lock-circle mr-2 text-success"></i>
              <span class="text-white small">You will be redirected to Paystack to complete your payment securely.</span>
            </div>
          </div>
        </template>
      </drawer-section>

      <!-- Email options (hidden in append mode — send_confirmation_email default true) -->
      <drawer-section v-if="!isAppendMode" title="Email options" icon="icon-email-85">
        <div class="email-option-row">
          <el-switch active-color="#e14eca" v-model="noConfirmationEmail" />
          <label class="ml-2 text-white">Do not send automated emails to the customer for this booking</label>
        </div>

        <div class="email-option-row">
          <el-switch
            active-color="#e14eca"
            :disabled="addPayment && partialAmount >= total"
            v-model="sendPaymentRequest"
          />
          <label class="ml-2 text-white">Send payment request email to customer</label>
        </div>
      </drawer-section>

      <!-- Action buttons -->
      <div class="form-actions">
        <base-button type="secondary" size="sm" @click="onCancelClick">
          {{ cancelLabel }}
        </base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="loading || isPriceEditing || (addPayment && !!errs.partial_amount)"
          :title="isPriceEditing ? 'Save or cancel the price override first' : (addPayment && errs.partial_amount ? errs.partial_amount : '')"
          @click="createBooking"
        >
          <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> {{ submitLoadingLabel }}</span>
          <span v-else>{{ submitLabel }}</span>
        </base-button>
      </div>
    </div>

    <!-- Item picker: catalog tiles + custom item form, shared with the
         post-create Add item view. Confirming stages the line locally;
         nothing is posted until Create booking. -->
    <item-picker
      v-if="itemPickerOpen"
      class="create-booking-view__picker"
      :company-id="companyId"
      :company-group-id="companyGroupId"
      :game-ids="pickerGameIds"
      :game-name="pickerGameName"
      :party-size="numberOfPlayers"
      :currency="currency"
      :currency-symbol="actualCurrencySymbol"
      :currency-format="Number(currency_format)"
      :number-format="Number(number_format)"
      confirm-label="Add to order"
      @cancel="itemPickerOpen = false"
      @confirm="onItemStaged"
    />
  </div>
</template>

<script>
import { isValidEmail as validateEmail } from '@/util/emailValidator';
import { buildCustomFieldResponsesPayload, filterCustomFieldsByGames } from '@/util/customFieldValidation';
import { isInternalPhoneRequired, validateInternalCustomerPhone } from '@/util/customerFieldValidation';
import PromoRulesService from '@/services/promoRulesService';
import {
  calculateSubtotal,
  filterPricingByDuration,
  calculateTaxBreakdown,
  calculateFeeAmount,
  calculateTotalFee,
  calculateLineDiscounts,
  calculateBuyXGetYDiscount,
  calculateBundlePriceDiscount,
  calculateExpectedDeposit,
  applyPrivateUpgrade,
  roundCents,
  toCents, sortDurations } from '@/util/pricing';
import {
  Switch,
  Select,
  Option,
  Autocomplete,
  InputNumber,
  Tooltip
} from 'element-ui';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import EnhancedPaymentForm from '@/components/Payments/EnhancedPaymentForm';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import _ from 'lodash';
import swal from 'sweetalert2';
import currencies from '@/assets/currencies.json';
import { formatCurrency } from '@/util/currencyFormat';
import { allowsOverpayment } from '@/util/paymentAmountCap';
import { appendModeState, updateAppendModeBalance, exitAppendMode } from '@/util/appendMode';
import { multiSelectState, removeSlotFromCart, exitMultiSelect } from '@/util/multiSelect';
import CartLine from '../components/CartLine.vue';
import ItemPicker from '../components/ItemPicker.vue';

export default {
  name: 'CreateBookingView',

  components: {
    PhoneNumberInput,
    EnhancedPaymentForm,
    CartLine,
    ItemPicker,
    [Switch.name]: Switch,
    [Select.name]: Select,
    [Option.name]: Option,
    [Autocomplete.name]: Autocomplete,
    [InputNumber.name]: InputNumber,
    [Tooltip.name]: Tooltip
  },

  props: {
    booking: { type: Object, default: () => ({}) },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' },
    currencySymbol: { type: String, default: '$' },
    countryCode: { type: String, default: 'US' },
    paymentSettings: { type: Object, default: () => ({}) },
    taxes: { type: Array, default: () => [] },
    fees: { type: Array, default: () => [] },
    pricingTypeId: { type: Number, default: 1 },
    resolvedPricing: { type: Object, default: null },
    // True when the slot already has at least one group booked. Disables the
    // upgrade-to-private toggle since you can't take over an in-progress slot.
    slotHasExistingBookings: { type: Boolean, default: false },
    // OTC-482: the form-mode booking request this booking approves. Prefills
    // the customer and party size; the transaction carries booking_request_id.
    assigningRequest: { type: Object, default: null }
  },

  data() {
    const group = JSON.parse(localStorage.getItem('group') || '{}');

    return {
      loading: false,
      //Multiple durations: the duration the staff member picked (null = default)
      selectedDurationId: null,
      authorize_opaque_data: null,
      lookupValue: '',
      internalCustomFields: [],
      customFieldResponses: {},
      loadingCustomFields: false,
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        phoneE164: '',
        end_user_id: null
      },
      pricingCategories: [],
      // True when the user toggles "Upgrade to private reservation". Only
      // available when the slot is public-bookable AND the game allows the
      // upgrade. Locks subtotal to reservation_upgrade_fee.
      upgradeToPrivate: false,
      // Cart mode: per-CartLine emissions, keyed by slotId. Each entry is
      // the payload from CartLine's `update` event:
      // { slotId, gameId, subtotal, numberOfPlayers, upgradeFee, tickets, ... }.
      // The aggregate `subtotal` / `numberOfPlayers` used by tax/fee/promo
      // math comes from summing this map.
      cartLineState: {},
      // OTC-2964: items staged for this order. Each entry is the payload
      // ItemPicker emits (catalog: id, name, price, quantity, available,
      // unlimited_available_quantity, price_override, original_price, amount;
      // custom: name, description, price, quantity, amount) plus a local key.
      stagedItems: [],
      itemPickerOpen: false,
      // Bookings-only subtotal. `subtotal` below is bookings + items and is
      // what taxes, fees, promos and the payload's top-level subtotal use;
      // the per-booking payload amounts keep using bookingSubtotal.
      bookingSubtotal: 0,
      subtotal: 0,
      totalTax: 0,
      taxBreakdown: [],
      inclusiveTax: 0,
      totalFee: 0,
      shelfDiscount: 0,
      bxgyPerLine: new Map(),
      total: 0,
      waiveTax: false,
      waiveFee: false,
      addPayment: false,
      partialAmount: 0,
      // 'deposit' | 'full' | 'custom' — drives the segmented toggle. Defaults
      // to 'deposit' whenever the slot has any deposit defined, mirroring the
      // customer-facing booking flow.
      paymentMode: 'full',
      // Tracks whether the staff explicitly picked a mode. Lets a late-loading
      // resolvedPricing flip the default to 'deposit' without overriding an
      // intentional choice.
      paymentModeUserSet: false,
      selectedPaymentOption: { name: '', content: '', id: '', is_default: '' },
      paymentOptions: [],
      noConfirmationEmail: false,
      sendPaymentRequest: false,
      giftCardCode: '',
      selectedGiftCard: null,
      availableCredit: 0,
      promoCode: '',
      selectedPromoCode: '',
      promos: [],
      stripe: null,
      elements: null,
      card: null,
      square: null,
      squareCard: null,
      payment: {
        cardNumber: '',
        expirationDate: '',
        cardCode: '',
        zip: ''
      },
      stripeCustomer: null,
      stripePaymentMethods: [],
      selectedPaymentMethod: 'new',
      selectedStoredCard: null,
      saveCardForFuture: false,
      errs: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        partial_amount: '',
        gift_card_code: ''
      },
      currencies: currencies.currencies,
      currency: group.currency || 'USD',
      number_format: group.number_format || 0,
      currency_format: group.currency_format || 0,
      gameData: null
    };
  },

  computed: {
    showDurationPicker() {
      const rp = this.resolvedPricing;
      return !this.isCartMode && !!(rp && rp.enable_multiple_durations && Array.isArray(rp.durations) && rp.durations.length > 1);
    },
    durationOptions() {
      const rp = this.resolvedPricing || {};
      return sortDurations(rp.durations || []).map(d => ({
        value: Number(d.is_default) === 1 ? null : Number(d.id),
        label: `${d.duration_minutes} min${Number(d.is_default) === 1 ? ' (default)' : ''}`
      }));
    },
    activeSlot() {
      return this.booking;
    },

    // Append mode: drawer was launched while staff is adding bookings to an
    // existing transaction. Customer + payment + promo + custom fields all live
    // on the parent transaction; here we only collect tickets and submit to
    // add-booking-to-transaction. State lives in src/util/appendMode.js so the
    // 5 calendar surfaces can also observe it for the banner + slot-click router.
    isAppendMode() {
      return appendModeState.isActive;
    },

    // Cart mode: drawer is composing a NEW transaction across N events
    // selected from the calendar. Mutually exclusive with append mode.
    // Stage B.1 only flips header/section titles; the multi-slot ticket
    // refactor lands in Stage B.2.
    isCartMode() {
      return multiSelectState.isActive;
    },

    cartSlotCount() {
      return (multiSelectState.cartSlots || []).length;
    },

    // Title strings — drawer's view-header h5 and the Tickets section
    // header. Append mode wins precedence over cart mode (they shouldn't
    // be active simultaneously, but if a regression toggles both, append
    // mode's transaction context is the more specific intent).
    viewHeaderTitle() {
      if (this.itemPickerOpen) return 'Add item';
      if (this.isAppendMode) return 'Add booking to transaction';
      if (this.isCartMode) return 'Create bookings';
      return 'Create booking';
    },

    ticketsSectionTitle() {
      if (this.isCartMode) return 'Cart';
      // Matches the booked slot drawer's section name: tickets, items and the
      // totals they make up live together here.
      return 'Transaction summary';
    },

    ticketsSectionIcon() {
      if (this.isCartMode) return 'icon-cart';
      return 'icon-money-coins';
    },

    submitLabel() {
      if (this.assigningRequest) return 'Book and approve request';
      if (this.isAppendMode) return 'Add booking';
      if (this.isCartMode) return 'Create bookings';
      return 'Create booking';
    },

    submitLoadingLabel() {
      if (this.isAppendMode) return 'Adding...';
      if (this.isCartMode) return 'Creating...';
      return 'Creating...';
    },

    // In cart mode the Cancel button bears the destructive cart-wide
    // intent — clicking it clears the cart AND closes the drawer (the
    // drawer's X button just closes; cart persists. This is the
    // explicit "scrap this in-progress cart" action).
    cancelLabel() {
      return this.isCartMode ? 'Cancel cart' : 'Cancel';
    },

    appendTargetTransactionId() {
      return appendModeState.transactionId;
    },

    // Surfaced in the view subtitle so staff can confirm which transaction
    // they're appending to without leaving the drawer. Falls back gracefully
    // if either field is missing.
    appendContextLabel() {
      if (!appendModeState.isActive) return '';
      const parts = [];
      if (appendModeState.customerName) parts.push(appendModeState.customerName);
      if (appendModeState.orderNumber) parts.push(`#${appendModeState.orderNumber}`);
      return parts.join(' · ');
    },

    bookingGameId() {
      return this.booking?.gameId || this.booking?.originalData?.game_id || null;
    },

    visibleCustomFields() {
      return filterCustomFieldsByGames(this.internalCustomFields, this.bookingGameId);
    },

    numberOfPlayers() {
      if (this.isCartMode) {
        return Object.values(this.cartLineState).reduce(
          (sum, line) => sum + (Number(line.numberOfPlayers) || 0),
          0
        );
      }
      return this.pricingCategories.reduce((sum, c) => sum + (c.number_of_players || 0), 0);
    },

    // The slot entries to render in cart mode. Each entry carries the
    // display fields captured by addSlotToCart (game name, date, time)
    // so CartLine can show its header without a separate fetch.
    cartSlots() {
      return multiSelectState.cartSlots || [];
    },

    // Aggregate per-line subtotals into the cart's working subtotal.
    // Used by recalculateTotal in cart mode; tax/fee/promo math
    // operates on this.subtotal unchanged.
    cartLineSubtotalSum() {
      return Object.values(this.cartLineState).reduce(
        (sum, line) => sum + (Number(line.subtotal) || 0),
        0
      );
    },

    // Show upgrade-to-private toggle only on public-bookable slots whose game
    // allows the upgrade AND the slot has no existing bookings yet. Once any
    // group has booked, the slot can't be flipped to a private reservation.
    // Mirrors Bookings.vue:926 plus the existing-bookings guard.
    canUpgradeToPrivate() {
      const rp = this.resolvedPricing || {};
      return !!(rp.is_public_booking && rp.allow_reservation_type_upgrade && !this.slotHasExistingBookings);
    },

    upgradeFee() {
      const rp = this.resolvedPricing || {};
      return parseFloat(rp.reservation_upgrade_fee || 0);
    },

    upgradePricingMode() {
      const rp = this.resolvedPricing || {};
      return rp.reservation_upgrade_pricing_mode || 'buyout';
    },

    isPriceEditing() {
      return this.pricingCategories?.some(c => c.editing) || false;
    },

    nonOptionalTaxes() {
      return this.taxes.filter(t => !t.optional);
    },

    // OTC-2964: experiences in context for the item picker (add-ons for them
    // sort first). Cart mode spans every slot in the cart.
    pickerGameIds() {
      if (this.isCartMode) {
        return this.cartSlots.map(s => Number(s.game_id)).filter(n => !Number.isNaN(n) && n > 0);
      }
      return this.bookingGameId ? [Number(this.bookingGameId)] : [];
    },

    pickerGameName() {
      if (this.isCartMode) return '';
      return this.booking?.gameName || this.booking?.originalData?.game_name || '';
    },

    itemsSubtotal() {
      return roundCents(this.stagedItems.reduce((sum, l) => sum + (Number(l.amount) || 0), 0));
    },

    // Line descriptors for the line-aware pricing helpers: one booking line
    // per slot, then one item line per staged item. Booking lineIds are the
    // slot ids so they share keys with the BXGY / bundle perLine maps.
    pricingLines() {
      const lines = [];
      if (this.isCartMode) {
        for (const line of Object.values(this.cartLineState)) {
          lines.push({
            lineId: line.slotId,
            kind: 'booking',
            gameId: line.gameId || null,
            itemId: null,
            amount: Number(line.subtotal) || 0,
            quantity: Number(line.numberOfPlayers) || 0,
            discount: 0
          });
        }
      } else {
        lines.push({
          lineId: this.booking?.id || 'draft',
          kind: 'booking',
          gameId: this.bookingGameId,
          itemId: null,
          amount: this.bookingSubtotal,
          quantity: this.numberOfPlayers,
          discount: 0
        });
      }
      for (const line of this.stagedItems) {
        lines.push({
          lineId: 'item-' + line.key,
          kind: 'item',
          gameId: null,
          itemId: line.id || null,
          amount: Number(line.amount) || 0,
          quantity: Number(line.quantity) || 0,
          discount: 0
        });
      }
      return lines;
    },

    bxgyCart() {
      if (this.isCartMode) {
        return Object.values(this.cartLineState).map(line => ({
          id: line.slotId,
          game_id: line.gameId || null,
          total: Number(line.subtotal) || 0,
          group_size: Number(line.numberOfPlayers) || 0
        }));
      }
      return [{
        id: this.booking?.id || 'draft',
        game_id: this.bookingGameId,
        total: this.bookingSubtotal,
        group_size: this.numberOfPlayers
      }];
    },

    bxgyItems() {
      return this.stagedItems.map(l => ({
        id: 'item-' + l.key,
        item_id: l.id || null,
        amount: Number(l.amount) || 0,
        quantity: Number(l.quantity) || 0
      }));
    },

    nonOptionalFees() {
      return this.fees.filter(f => !f.optional);
    },

    currentGameId() {
      return this.booking?.gameId || this.booking?.originalData?.game_id || null;
    },

    actualCurrencySymbol() {
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      return currencyInfo ? currencyInfo.symbol : this.currencySymbol;
    },

    selectedGame() {
      // Use fetched game data for full pricing info
      if (this.gameData) return this.gameData;
      if (!this.booking) return null;
      const raw = this.booking.originalData || this.booking;
      return {
        name: this.booking.gameName || raw.game_name,
        game_pricing: raw.game_pricing || raw.price || raw.pricing
      };
    },

    expectedDeposit() {
      return calculateExpectedDeposit(this.resolvedPricing, this.numberOfPlayers);
    },

    // Cap the configured deposit at the slot total — when a per-participant
    // deposit × player count exceeds total (over-configured), we still want
    // to surface the Deposit affordance but never charge more than what's
    // actually owed.
    depositChargeAmount() {
      if (this.expectedDeposit <= 0) return 0;
      return Math.min(this.expectedDeposit, this.total);
    },

    depositApplicable() {
      return this.depositChargeAmount > 0 && this.total > 0;
    },

    // Cash and other record-only methods may exceed the total; the negative
    // due is the change owed back. Card, gift card and credit keep the cap.
    canExceedTotal() {
      return allowsOverpayment(this.selectedPaymentOption && this.selectedPaymentOption.name);
    },

    depositRequired() {
      return this.depositApplicable && !!(this.resolvedPricing && this.resolvedPricing.require_deposit);
    },

    paymentModeOptions() {
      const options = [];
      if (this.depositApplicable) {
        options.push({ value: 'deposit', label: `Deposit (${this.getAmount(this.depositChargeAmount)})` });
      }
      options.push({ value: 'full', label: `Full (${this.getAmount(this.total)})` });
      options.push({ value: 'custom', label: 'Custom' });
      return options;
    }
  },

  watch: {
    // Re-check applied promos when the participant count changes: a promo that
    // qualified at apply time can fall outside its quantity rule afterwards.
    numberOfPlayers() {
      this.revalidateAppliedPromos();
    },
    // Prune cartLineState entries for slots that have been removed from
    // the cart (either via CartLine's remove button or by exiting cart
    // mode entirely, which clears multiSelectState.cartSlots). Otherwise
    // stale subtotals would linger in the aggregate.
    cartSlots: {
      handler(newSlots) {
        const liveIds = new Set((newSlots || []).map(s => String(s.id)));
        const staleKeys = Object.keys(this.cartLineState).filter(k => !liveIds.has(k));
        if (staleKeys.length > 0) {
          for (const k of staleKeys) this.$delete(this.cartLineState, k);
          this.recalculateTotal();
        }
      }
    },
    booking: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.resetForm();
          if (!this.resolvedPricing) {
            await this.fetchGameData();
          }
          this.seedSelectedDuration();
          this.loadPricingCategories();
          this.applyAssigningRequest();
          this.recalculateTotal();
          this.loadPaymentOptions();
          this.fetchInternalCustomFields();
        }
      }
    },

    pricingCategories: {
      handler() {
        this.recalculateTotal();
      },
      deep: true
    },

    // Catch the late-loading case: if resolvedPricing or player counts arrive
    // after the staff already toggled "Take payment now", flip the default to
    // Deposit. Skipped once staff has explicitly picked a mode.
    expectedDeposit() {
      if (!this.addPayment) return;
      if (this.paymentModeUserSet) return;
      if (this.depositApplicable && this.paymentMode !== 'deposit') {
        this.paymentMode = 'deposit';
        this.partialAmount = this.depositChargeAmount;
        this.validatePartialAmount();
      }
    },

    selectedPaymentOption: {
      handler(newVal) {
        if (this.addPayment && newVal && newVal.name === 'Card') {
          this.$nextTick(() => {
            this.initializePaymentForm();
          });
        }
        if (this.addPayment && this.partialAmount > 0) {
          this.validatePartialAmount();
        }
      },
      deep: true
    }
  },

  methods: {
    isPhoneRequired() {
      try {
        return isInternalPhoneRequired(JSON.parse(localStorage.getItem('group') || '{}'));
      } catch (e) {
        return false;
      }
    },

    // Cancel-button click. In single-slot mode, dismisses the create-
    // booking view back to detail (existing behavior). In cart mode,
    // the button is the destructive "scrap this cart" action — clear
    // the cart, exit cart mode, and emit a fresh `cancel-cart` event
    // so the drawer shell can close itself entirely (the drawer's own
    // X button just closes without clearing — this is the explicit
    // clearing path).
    onCancelClick() {
      if (this.isCartMode) {
        exitMultiSelect();
        this.cartLineState = {};
        this.stagedItems = [];
        this.$emit('cancel-cart');
        return;
      }
      this.$emit('back');
    },

    // Header back arrow: closes the item picker first, otherwise leaves the view.
    onBackClick() {
      if (this.itemPickerOpen) {
        this.itemPickerOpen = false;
        return;
      }
      this.$emit('back');
    },

    // OTC-2964: a line confirmed in the item picker. The server stores the
    // rounded cents figure, so the staged line starts from the same number.
    onItemStaged(payload) {
      const { line_total, ...line } = payload; // eslint-disable-line no-unused-vars
      this.stagedItems.push({
        key: Date.now() + '-' + this.stagedItems.length,
        ...line,
        amount: roundCents((parseFloat(line.price) || 0) * (parseInt(line.quantity, 10) || 0))
      });
      this.itemPickerOpen = false;
      this.recalculateTotal();
    },

    removeStagedItem(key) {
      this.stagedItems = this.stagedItems.filter(l => l.key !== key);
      this.recalculateTotal();
    },

    // Payload shape is the one add-item-to-transaction already accepts;
    // create-transaction-for-group-v2 reads the same keys (catalog: id,
    // quantity, price, price_override; custom: name, description, price, quantity).
    itemsPayload() {
      return this.stagedItems.map(l => {
        if (l.id) {
          return {
            id: l.id,
            name: l.name,
            price: l.price,
            quantity: l.quantity,
            available: l.available,
            unlimited_available_quantity: l.unlimited_available_quantity,
            price_override: l.price_override,
            original_price: l.original_price,
            amount: l.amount
          };
        }
        return {
          name: l.name,
          description: l.description || '',
          price: l.price,
          quantity: l.quantity,
          amount: l.amount
        };
      });
    },

    // Cart-mode handlers. Each CartLine owns its own pricing fetch +
    // category state + upgrade toggle, and emits its computed line
    // subtotal here whenever anything changes. We stash by slotId and
    // let recalculateTotal sum into the cart's aggregate.
    onCartLineUpdate(payload) {
      if (!payload || payload.slotId == null) return;
      this.$set(this.cartLineState, String(payload.slotId), payload);
      this.recalculateTotal();
    },

    onCartLineRemove(slotId) {
      removeSlotFromCart(slotId);
      this.$delete(this.cartLineState, String(slotId));
      this.recalculateTotal();
    },

    getFeeAmount(fee) {
      if (this.isCartMode) {
        // Cart lines can belong to different events, so a linked fee must be
        // judged per line, the same way recalculateTotal charges it. Judging
        // it by the anchor slot alone hid the row while the total still
        // carried the amount.
        return calculateTotalFee([fee], this.subtotal, this.numberOfPlayers, false, { discount: this.shelfDiscount, gameId: this.currentGameId, lines: this.pricingLines });
      }
      return calculateFeeAmount(fee, this.subtotal, this.numberOfPlayers, { discount: this.shelfDiscount, gameId: this.currentGameId });
    },

    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.idToken.jwtToken;
    },

    async fetchGameData() {
      const gameId = this.booking?.gameId || this.booking?.originalData?.game_id;
      if (!gameId) return;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games`;
        const response = await axios.get(url, config);
        const games = response.data || [];
        this.gameData = games.find(g => g.id === gameId) || null;
      } catch (error) {
        console.error('Failed to fetch game data:', error);
        this.gameData = null;
      }
    },

    customFieldResponsesPayload() {
      return buildCustomFieldResponsesPayload(this.visibleCustomFields, this.customFieldResponses);
    },
    async fetchInternalCustomFields() {
      this.loadingCustomFields = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/custom-fields/internal`;
        const response = await axios.get(url, config);
        this.internalCustomFields = (response.data && response.data.internal_custom_fields) || [];
      } catch (error) {
        console.error('Failed to fetch internal custom fields:', error);
        this.internalCustomFields = [];
      } finally {
        this.loadingCustomFields = false;
      }
    },

    resetForm() {
      this.lookupValue = '';
      this.user = { first_name: '', last_name: '', email: '', phone: '', phoneE164: '', end_user_id: null };
      this.pricingCategories = [];
      this.subtotal = 0;
      this.totalTax = 0;
      this.totalFee = 0;
      this.shelfDiscount = 0;
      this.total = 0;
      this.waiveTax = false;
      this.waiveFee = false;
      this.addPayment = false;
      this.partialAmount = 0;
      this.paymentMode = 'full';
      this.paymentModeUserSet = false;
      this.selectedPaymentOption = { name: '', content: '', id: '', is_default: '' };
      this.noConfirmationEmail = false;
      this.sendPaymentRequest = false;
      this.giftCardCode = '';
      this.selectedGiftCard = null;
      this.availableCredit = 0;
      this.promoCode = '';
      this.selectedPromoCode = '';
      this.promos = [];
      this.customFieldResponses = {};
      this.errs = { first_name: '', last_name: '', email: '', phone: '', partial_amount: '', gift_card_code: '' };
    },

    // ============================================
    // Pricing
    // ============================================
    //Multiple durations: the default duration is the null id; nothing to pick when the feature is off
    seedSelectedDuration() {
      this.selectedDurationId = null;
    },
    onDurationChange() {
      this.loadPricingCategories();
      this.recalculateTotal();
    },
    loadPricingCategories() {
      const slot = this.booking;
      if (!slot) return;

      const raw = slot.originalData || slot;
      let pricing = [];

      // Use resolved pricing from drawer endpoint when available (already has correct tiers)
      if (this.resolvedPricing && this.resolvedPricing.tiers && this.resolvedPricing.tiers.length > 0) {
        pricing = filterPricingByDuration(this.resolvedPricing.tiers, this.selectedDurationId);
        this.pricingCategories = [];
        for (let n in pricing) {
          if (!_.find(this.pricingCategories, { category_id: pricing[n].category_id })) {
            this.pricingCategories.push({
              category_id: pricing[n].category_id,
              category: pricing[n].category,
              price: pricing[n].price,
              number_of_players: pricing[n].default_participants || 0,
              category_pricing: pricing.filter(p => p.category_id === pricing[n].category_id)
            });
          }
        }
        this.pricingCategories.sort((a, b) => a.category_id - b.category_id);
        this.pricingCategories = [...this.pricingCategories];
        return;
      }

      let customPricing = raw.custom_pricing;
      if (typeof customPricing === 'string') {
        try { customPricing = JSON.parse(customPricing); } catch (e) { customPricing = []; }
      }
      const hasCustomPricing = Array.isArray(customPricing) && customPricing.length > 0;

      if (raw.inherited_pricing && raw.inherited_pricing.length > 0) {
        this.pricingCategories = raw.inherited_pricing.map(p => ({
          category_id: p.category_id,
          category: p.category,
          price: p.price,
          original_price: p.original_price,
          number_of_players: p.default_participants || 0
        }));
        this.pricingCategories.sort((a, b) => a.category_id - b.category_id);
      } else if (raw.is_custom_pricing_enabled && hasCustomPricing) {
        pricing = customPricing;
        this.pricingCategories = [];
        for (let n in pricing) {
          if (!_.find(this.pricingCategories, { category_id: pricing[n].category_id })) {
            this.pricingCategories.push({
              category_id: pricing[n].category_id,
              category: pricing[n].category,
              price: pricing[n].price,
              number_of_players: pricing[n].default_participants || 0,
              category_pricing: pricing.filter(p => p.category_id === pricing[n].category_id)
            });
          }
        }
        this.pricingCategories.sort((a, b) => a.category_id - b.category_id);
      } else {
        // Try slot data first, then fall back to fetched game data
        let pricingSource = raw.pricing_categories || raw.pricing || raw.price || raw.game_pricing;
        if (!pricingSource && this.gameData && this.gameData.game_pricing) {
          pricingSource = this.gameData.game_pricing;
        }

        if (pricingSource) {
          pricing = typeof pricingSource === 'string' ? JSON.parse(pricingSource) : pricingSource;
        }

        if (pricing && pricing.length > 0) {
          this.pricingCategories = [];
          for (let n in pricing) {
            if (!_.find(this.pricingCategories, { category_id: pricing[n].category_id })) {
              this.pricingCategories.push({
                category_id: pricing[n].category_id,
                category: pricing[n].category,
                price: pricing[n].price,
                number_of_players: pricing[n].default_participants || 0,
                category_pricing: pricing.filter(p => p.category_id === pricing[n].category_id),
                pricing: pricing.filter(p => p.category_id === pricing[n].category_id)
              });
            }
          }
          this.pricingCategories.sort((a, b) => a.category_id - b.category_id);
        } else {
          this.pricingCategories = [{
            category_id: 1,
            category: 'Player',
            price: raw.per_player_price || 0,
            number_of_players: 0
          }];
        }
      }

      this.pricingCategories = [...this.pricingCategories];
    },

    recalculateTotal() {
      // Cart mode: subtotal is the aggregate of per-CartLine emissions.
      // Each CartLine owns its own pricing + categories + upgrade toggle
      // and emits its computed line subtotal via @update. We sum them
      // here and let the existing tax/fee/promo/total math (below) run
      // on the aggregate. Skips the single-slot pricing resolution path
      // entirely — it would have nothing to compute against in cart mode.
      if (this.isCartMode) {
        this.bookingSubtotal = roundCents(this.cartLineSubtotalSum);
      } else {
        const slot = this.booking;
        if (!slot) {
          this.bookingSubtotal = 0;
          this.subtotal = 0;
          this.total = 0;
          return;
        }

        const raw = slot.originalData || slot;
        const rp = this.resolvedPricing;
        const game = this.gameData || {};

        // Use resolved pricing from drawer when available
        let pricingTypeId, cumulativePricing, pricing = [];

        if (rp && rp.tiers && rp.tiers.length > 0) {
          pricingTypeId = rp.pricing_type_id || this.pricingTypeId || 1;
          cumulativePricing = rp.cumulative_category_pricing || false;
          pricing = filterPricingByDuration(rp.tiers, this.selectedDurationId);
        } else {
          const isCustom = raw.is_custom_pricing_enabled;
          pricingTypeId = isCustom
            ? (raw.pricing_type_id || this.pricingTypeId || 1)
            : (game.pricing_type_id || raw.pricing_type_id || this.pricingTypeId || 1);
          cumulativePricing = isCustom
            ? (raw.cumulative_category_pricing || false)
            : (game.cumulative_category_pricing || raw.cumulative_category_pricing || false);

          let customPricing = raw.custom_pricing;
          if (typeof customPricing === 'string') {
            try { customPricing = JSON.parse(customPricing); } catch (e) { customPricing = []; }
          }
          const hasCustomPricing = Array.isArray(customPricing) && customPricing.length > 0;

          if (raw.is_custom_pricing_enabled && hasCustomPricing) {
            pricing = customPricing;
          } else if (this.selectedGame && this.selectedGame.game_pricing) {
            pricing = typeof this.selectedGame.game_pricing === 'string'
              ? JSON.parse(this.selectedGame.game_pricing)
              : this.selectedGame.game_pricing;
          }
        }

        // Upgrade-to-private routes through applyPrivateUpgrade: 'buyout' replaces
        // the per-ticket total with the flat fee; 'tickets_plus_fee' adds the
        // nominal fee on top of normal per-ticket pricing.
        const result = calculateSubtotal({
          pricing,
          categories: this.pricingCategories,
          cumulativePricing,
          pricingTypeId
        });
        const base = roundCents(parseFloat(result.subtotal));
        if (this.upgradeToPrivate) {
          this.bookingSubtotal = roundCents(applyPrivateUpgrade({ baseSubtotal: base, mode: this.upgradePricingMode, fee: this.upgradeFee }));
        } else {
          this.bookingSubtotal = base;
        }
      }
      // Order subtotal: bookings plus staged items. Everything transaction
      // level (taxes, fees, promos, total, payload subtotal) reads this.
      this.subtotal = roundCents(this.bookingSubtotal + this.itemsSubtotal);

      // Calculate taxes with full compound/linked support
      const gameId = this.booking?.gameId || this.booking?.originalData?.game_id || null;
      // Promo math per line (bookings and items), mirroring update-transaction-values.
      const lines = this.pricingLines;
      const lineDiscounts = calculateLineDiscounts({ promos: this.promos, lines, taxes: this.nonOptionalTaxes });
      const flatDiscountResult = lineDiscounts;

      // BXGY and bundle rules evaluate against one cart entry per slot plus the
      // staged items, the same shape the backend evaluator sees.
      const bxgyCart = this.bxgyCart;
      const bxgyResult = calculateBuyXGetYDiscount({
        promos: this.promos,
        cart: bxgyCart,
        items: this.bxgyItems,
        taxes: this.nonOptionalTaxes
      });
      const bundleResult = calculateBundlePriceDiscount({
        promos: this.promos,
        cart: bxgyCart,
        items: this.bxgyItems,
        taxes: this.nonOptionalTaxes
      });
      this.bxgyPerLine = bxgyResult.perLine;
      for (const [lineId, amounts] of bundleResult.perLine) {
        const existing = this.bxgyPerLine.get(lineId) || { exTax: 0, shelf: 0 };
        this.bxgyPerLine.set(lineId, {
          exTax: roundCents(existing.exTax + amounts.exTax),
          shelf: roundCents(existing.shelf + amounts.shelf)
        });
      }

      let exTaxDiscount = roundCents(flatDiscountResult.exTaxDiscount + bxgyResult.exTaxDiscount + bundleResult.exTaxDiscount);
      let shelfDiscount = roundCents(flatDiscountResult.shelfDiscount + bxgyResult.shelfDiscount + bundleResult.shelfDiscount);
      if (exTaxDiscount > this.subtotal) exTaxDiscount = this.subtotal;
      if (shelfDiscount > this.subtotal) shelfDiscount = this.subtotal;
      this.shelfDiscount = shelfDiscount;

      // Per-line ex-tax discounts feed the per-line tax and fee value types.
      const linesWithDiscount = lines.map(l => ({
        ...l,
        discount: roundCents(
          (lineDiscounts.perLine.get(l.lineId)?.exTax || 0) + (this.bxgyPerLine.get(l.lineId)?.exTax || 0)
        )
      }));

      // Pass ex-tax discount to tax calculation so inclusive tax base is correct
      const breakdown = calculateTaxBreakdown({
        taxes: this.nonOptionalTaxes,
        subtotal: this.subtotal,
        numberOfPlayers: this.numberOfPlayers,
        discount: exTaxDiscount,
        gameId,
        waiveTax: this.waiveTax,
        lines: linesWithDiscount
      });
      this.totalTax = breakdown.totalTax;
      this.taxBreakdown = breakdown.taxAmounts;

      // Track inclusive tax separately for the submission payload
      this.inclusiveTax = 0;
      for (const entry of breakdown.taxAmounts) {
        if (entry.tax.pricing_type === 1) {
          this.inclusiveTax += entry.amount;
        }
      }
      this.inclusiveTax = roundCents(this.inclusiveTax);

      // Calculate fees
      // Backend fee base is subtotal minus the ex-tax discount (update-transaction-values totalDiscountValue).
      this.totalFee = calculateTotalFee(this.nonOptionalFees, this.subtotal, this.numberOfPlayers, this.waiveFee, { discount: exTaxDiscount, gameId: this.currentGameId, lines: linesWithDiscount });

      // Reconcile: shelf discount = ex-tax discount + inclusive tax savings
      let reconciledSavings = roundCents(shelfDiscount - exTaxDiscount);
      if (reconciledSavings < 0) reconciledSavings = 0;
      this.total = roundCents(this.subtotal + this.totalTax + this.totalFee - exTaxDiscount - reconciledSavings);
      if (this.total < 0) this.total = 0;

      if (this.addPayment) {
        // Re-anchor partialAmount whenever totals shift (ticket count, promo,
        // tax waiver). Deposit mode follows expectedDeposit; full mode follows
        // total; custom mode leaves the staff-entered value alone but clamps
        // to the new total ceiling.
        // When a big promo drops total below expectedDeposit, the deposit
        // affordance disappears and partialAmount would otherwise be stuck
        // above total — fall back to full so the visible amount stays valid.
        if (this.paymentMode === 'deposit' && this.depositApplicable) {
          this.partialAmount = this.depositChargeAmount;
        } else if (this.paymentMode === 'deposit') {
          this.paymentMode = 'full';
          this.partialAmount = this.total;
        } else if (this.paymentMode === 'full') {
          this.partialAmount = this.total;
        } else if (!this.canExceedTotal && this.partialAmount > this.total) {
          this.partialAmount = this.total;
        }
      }
    },

    getDiscountAmount(promo) {
      const flat = calculateLineDiscounts({ promos: [promo], lines: this.pricingLines, taxes: this.nonOptionalTaxes });
      const bxgy = calculateBuyXGetYDiscount({
        promos: [promo],
        cart: this.bxgyCart,
        items: this.bxgyItems,
        taxes: this.nonOptionalTaxes
      });
      const bundle = calculateBundlePriceDiscount({
        promos: [promo],
        cart: this.bxgyCart,
        items: this.bxgyItems,
        taxes: this.nonOptionalTaxes
      });

      const shelfDiscount = roundCents(Math.min(this.subtotal, flat.shelfDiscount + bxgy.shelfDiscount + bundle.shelfDiscount));
      promo.applied_discount = shelfDiscount;
      return shelfDiscount;
    },

    getLineTotal(category) {
      if (!category || category.number_of_players === 0) return 0;
      const slot = this.booking;
      const raw = slot ? (slot.originalData || slot) : {};
      const rp = this.resolvedPricing;
      const game = this.gameData || {};
      let pricingTypeId;
      if (rp && rp.tiers && rp.tiers.length > 0) {
        pricingTypeId = rp.pricing_type_id || this.pricingTypeId || 1;
      } else {
        const isCustom = raw.is_custom_pricing_enabled;
        pricingTypeId = isCustom
          ? (raw.pricing_type_id || this.pricingTypeId || 1)
          : (game.pricing_type_id || raw.pricing_type_id || this.pricingTypeId || 1);
      }
      const unitPrice = this.getUnitPrice(category);
      return pricingTypeId === 1 ? unitPrice * category.number_of_players : unitPrice;
    },

    getUnitPrice(category) {
      const slot = this.booking;
      if (!slot) return category.price || 0;

      const raw = slot.originalData || slot;
      const rp = this.resolvedPricing;
      const game = this.gameData || {};
      let cumulativePricing, pricing = [];

      if (rp && rp.tiers && rp.tiers.length > 0) {
        cumulativePricing = rp.cumulative_category_pricing || false;
        pricing = rp.tiers;
      } else {
        const isCustom = raw.is_custom_pricing_enabled;
        cumulativePricing = isCustom
          ? (raw.cumulative_category_pricing || false)
          : (game.cumulative_category_pricing || raw.cumulative_category_pricing || false);

        if (isCustom && raw.custom_pricing) {
          pricing = typeof raw.custom_pricing === 'string' ? JSON.parse(raw.custom_pricing) : raw.custom_pricing;
        } else {
          pricing = category.pricing || category.category_pricing || [];
        }
      }

      if (pricing.length === 0) return category.price || 0;

      const checkPlayers = cumulativePricing ? this.numberOfPlayers : category.number_of_players;

      // Sort by min_players to match calc path (util/pricing.js: findPriceForPlayerCount)
      const categoryTiers = _.sortBy(
        pricing.filter(p => p.category_id === category.category_id),
        'min_players'
      );

      for (const tier of categoryTiers) {
        if (checkPlayers >= tier.min_players && checkPlayers <= tier.max_players) {
          return category.hasOwnProperty('original_price') ? category.price : tier.price;
        }
      }

      if (categoryTiers.length > 0) {
        const lastTier = categoryTiers[categoryTiers.length - 1];
        if (checkPlayers > lastTier.max_players) {
          return category.hasOwnProperty('original_price') ? category.price : lastTier.price;
        }
        if (checkPlayers > 0 && checkPlayers < categoryTiers[0].min_players) {
          return category.hasOwnProperty('original_price') ? category.price : categoryTiers[0].price;
        }
      }

      return category.price || 0;
    },

    getRate(category) {
      const categoryName = category?.category || 'person';
      const slot = this.booking;
      if (!slot) {
        return this.pricingTypeId === 1
          ? `${this.getAmount(category?.price || 0)} per ${categoryName.toLowerCase()}`
          : `${this.getAmount(category?.price || 0)} flat rate`;
      }

      const raw = slot.originalData || slot;
      const rp = this.resolvedPricing;
      const game = this.gameData || {};
      let pricingTypeId, cumulativePricing, pricing = [];

      if (rp && rp.tiers && rp.tiers.length > 0) {
        pricingTypeId = rp.pricing_type_id || this.pricingTypeId || 1;
        cumulativePricing = rp.cumulative_category_pricing || false;
        pricing = rp.tiers;
      } else {
        const isCustom = raw.is_custom_pricing_enabled;
        pricingTypeId = isCustom
          ? (raw.pricing_type_id || this.pricingTypeId || 1)
          : (game.pricing_type_id || raw.pricing_type_id || this.pricingTypeId || 1);
        cumulativePricing = isCustom
          ? (raw.cumulative_category_pricing || false)
          : (game.cumulative_category_pricing || raw.cumulative_category_pricing || false);

        let customPricing = raw.custom_pricing;
        if (typeof customPricing === 'string') {
          try { customPricing = JSON.parse(customPricing); } catch (e) { customPricing = []; }
        }
        const hasCustomPricing = Array.isArray(customPricing) && customPricing.length > 0;

        if (isCustom && hasCustomPricing) {
          pricing = customPricing;
        } else if (this.selectedGame && this.selectedGame.game_pricing) {
          pricing = typeof this.selectedGame.game_pricing === 'string'
            ? JSON.parse(this.selectedGame.game_pricing)
            : this.selectedGame.game_pricing;
        }
      }

      if (!pricing || pricing.length === 0) {
        return pricingTypeId === 1
          ? `${this.getAmount(category.price)} per ${categoryName.toLowerCase()}`
          : `${this.getAmount(category.price)} flat rate`;
      }

      const checkPlayers = cumulativePricing ? this.numberOfPlayers : category.number_of_players;

      // Sort by min_players so display walks tiers in the same order as the
      // calc path (util/pricing.js: findPriceForPlayerCount). Without this,
      // overlapping or non-min-ordered tiers can have the display land on a
      // different tier than the line total — the once-seen $55-rate / $184-total
      // edge case in OTC-2287.
      const sortedPricing = _.sortBy(pricing, 'min_players');

      // Find matching tier
      for (const tier of sortedPricing) {
        if (tier.category_id === category.category_id &&
            checkPlayers >= tier.min_players &&
            checkPlayers <= tier.max_players) {
          const price = category.hasOwnProperty('original_price') ? category.price : tier.price;
          return pricingTypeId === 1
            ? `${this.getAmount(price)} per ${categoryName.toLowerCase()}`
            : `${this.getAmount(price)} flat rate for ${tier.min_players}-${tier.max_players} ${categoryName.toLowerCase()}`;
        }
      }

      // Above max tier — last tier (in min_players order) for this category
      const lastTier = _.findLast(sortedPricing, { category_id: category.category_id });
      if (lastTier && checkPlayers > lastTier.max_players) {
        const price = category.hasOwnProperty('original_price') ? category.price : lastTier.price;
        return pricingTypeId === 1
          ? `${this.getAmount(price)} per ${categoryName.toLowerCase()}`
          : `${this.getAmount(price)} flat rate for ${lastTier.min_players}-${lastTier.max_players} ${categoryName.toLowerCase()}`;
      }

      // Below min tier — first tier (in min_players order) for this category
      const smallestTier = _.find(sortedPricing, { category_id: category.category_id });
      if (smallestTier && checkPlayers !== 0 && checkPlayers < smallestTier.min_players) {
        const price = category.hasOwnProperty('original_price') ? category.price : smallestTier.price;
        return pricingTypeId === 1
          ? `${this.getAmount(price)} per ${categoryName.toLowerCase()}`
          : `${this.getAmount(price)} flat rate for ${smallestTier.min_players}-${smallestTier.max_players} ${categoryName.toLowerCase()}`;
      }

      return pricingTypeId === 1
        ? `${this.getAmount(category.price)} per ${categoryName.toLowerCase()}`
        : `${this.getAmount(category.price)} flat rate`;
    },

    // ============================================
    // Price editing
    // ============================================
    startEditPrice(category) {
      // `$set` (not bare assignment) so Vue 2 makes original_price reactive.
      // Bare assignment leaves it non-observed and lets the subtotal computed
      // stay cached at the pre-override (tier × qty) value.
      //
      // Snapshot the pre-edit state so the red ✕ (cancel) can return to it
      // even when the row was already overridden before this edit. Without
      // the snapshot, cancel and restore collapse to the same behavior and
      // a re-edit of a prior override silently loses it on cancel.
      this.$set(category, '_preEditPrice', category.price);
      this.$set(category, '_preEditOverride', category.original_price);
      if (category.original_price === undefined) {
        this.$set(category, 'original_price', category.price);
      }
      this.$set(category, 'editing', true);
      this.$forceUpdate();
    },

    saveEditPrice(category) {
      // Commit: drop the snapshot.
      this.$delete(category, '_preEditPrice');
      this.$delete(category, '_preEditOverride');
      this.$set(category, 'editing', false);
      this.recalculateTotal();
      this.$forceUpdate();
    },

    cancelEditPrice(category) {
      // Return to the exact state captured at startEditPrice. Preserves a
      // prior override (or its absence); restoreOriginalPrice is the one
      // that always clears the override.
      if (category._preEditPrice !== undefined) {
        category.price = category._preEditPrice;
      }
      if (category._preEditOverride !== undefined) {
        this.$set(category, 'original_price', category._preEditOverride);
      } else {
        this.$delete(category, 'original_price');
      }
      this.$delete(category, '_preEditPrice');
      this.$delete(category, '_preEditOverride');
      this.$set(category, 'editing', false);
      this.recalculateTotal();
      this.$forceUpdate();
    },

    restoreOriginalPrice(category) {
      // Always clear the override and use the tier price, then exit edit
      // mode. Drops the snapshot too — once override is cleared, there's
      // nothing to cancel back to.
      if (category.original_price !== undefined) {
        category.price = category.original_price;
        this.$delete(category, 'original_price');
      }
      this.$delete(category, '_preEditPrice');
      this.$delete(category, '_preEditOverride');
      this.$set(category, 'editing', false);
      this.recalculateTotal();
      this.$forceUpdate();
    },

    // ============================================
    // Promo codes
    // ============================================
    async promoSearch(queryString, cb) {
      const slot = this.booking;
      if (!slot) { cb([]); return; }

      const raw = slot.originalData || slot;
      let bookingDate = this.extractDate(raw.booking_date);
      let purchaseDate = new Date();
      purchaseDate = purchaseDate.getFullYear() + '-' +
        ('0' + (purchaseDate.getMonth() + 1)).slice(-2) + '-' +
        ('0' + purchaseDate.getDate()).slice(-2);

      let dayOfWeek = '';
      if (bookingDate) {
        const parts = bookingDate.split('-');
        const dt = new Date(parts[0], parts[1] - 1, parts[2]);
        dayOfWeek = dt.getDay();
      }

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const gameName = slot.gameName || slot.game_name || raw.game_name || '';
        const startTime = raw.start_time || '';

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/promos?autocomplete=${encodeURIComponent(queryString)}&purchase_date=${purchaseDate}&event_date=${bookingDate}&event_time=${encodeURIComponent(startTime)}&day_of_week=${dayOfWeek}`;

        const response = await axios.get(url, config);
        let suggestions = [];

        if (response.data.hasOwnProperty('gift_card')) {
          this.selectedPromoCode = response.data.gift_card;
        }

        for (let n in response.data.promos) {
          const promo = response.data.promos[n];
          if (promo.name != null) {
            let dateAllowed = true;
            if (promo.event_exlusions) {
              const dates = promo.event_exlusions.split(',');
              for (let k in dates) {
                if (bookingDate === dates[k]) dateAllowed = false;
              }
            }

            // Default-deny: matches backend update-transaction-values, which only
            // applies a promo when is_cross_group OR there's an explicit game link.
            // A promo with no links arrives as promo.games === null; leaving the
            // default true silently lets it through autocomplete and the backend
            // then zeroes the discount on save.
            let gameAllowed = false;
            if (promo.is_cross_group) {
              gameAllowed = true;
            } else if (promo.games) {
              const promoGames = typeof promo.games === 'string' ? JSON.parse(promo.games) : promo.games;
              for (let m in promoGames) {
                if (promoGames[m].name === gameName) {
                  gameAllowed = true;
                  break;
                }
              }
            }
            // OTC-2964: a promo linked to a staged item is offered too; the
            // per-line math only discounts the lines it actually links.
            if (!gameAllowed && promo.items && this.stagedItems.length > 0) {
              let promoItems = [];
              try { promoItems = typeof promo.items === 'string' ? JSON.parse(promo.items) : promo.items; } catch (e) { promoItems = []; }
              const stagedIds = this.stagedItems.map(l => Number(l.id)).filter(n => n > 0);
              if (Array.isArray(promoItems) && promoItems.some(pi => stagedIds.includes(Number(pi.id)))) {
                gameAllowed = true;
              }
            }

            if (gameAllowed && dateAllowed) {
              suggestions.push({
                value: promo.name,
                id: promo.id,
                name: promo.name,
                description: promo.description,
                value_amount: promo.value_amount,
                redemption_type: promo.redemption_type,
                value_type: promo.value_type,
                internal_only: promo.internal_only,
                is_combineable: promo.is_combineable,
                is_cross_group: promo.is_cross_group,
                // Link arrays (JSON strings of {id, name}) so per-line promo
                // math can tell which bookings and items the promo reaches.
                games: promo.games,
                items: promo.items,
                rules: promo.rules || []
              });
            }
          }
        }

        cb(suggestions);
      } catch (error) {
        console.error('Error searching promos:', error);
        cb([]);
      }
    },

    handlePromoSelect(item) {
      this.promoCode = item.name;
      this.selectedPromoCode = item;
    },

    hasBuyXGetYRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'BUY_X_GET_Y');
    },
    hasMinValueRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'MIN_VALUE');
    },
    hasBundlePriceRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'BUNDLE_PRICE');
    },

    applyPromoCode() {
      if (this.selectedPromoCode && this.selectedPromoCode.hasOwnProperty('id')) {
        let dupe = 0;
        for (let n in this.promos) {
          if (this.selectedPromoCode.id === this.promos[n].id) dupe++;
          if (this.promos[n].is_combineable === 0 || this.selectedPromoCode.is_combineable === 0) {
            swal('Error', 'You cannot apply multiple non-combineable promo codes', 'error');
            return;
          }
        }

        if (dupe === 0) {
          // Enforce the promo's eligibility rules against the current booking
          // (gift cards carry a `code` and have no rules to evaluate).
          if (!this.selectedPromoCode.hasOwnProperty('code')) {
            const eligibility = PromoRulesService.evaluateCartEligibility(this.selectedPromoCode, {
              participants: this.numberOfPlayers,
              lineItems: 1,
              uniqueItems: 1
            });
            if (!eligibility.valid) {
              swal('Promo code requirements not met', eligibility.error_message, 'error');
              return;
            }
          }

          // Handle gift card
          if (this.selectedPromoCode.hasOwnProperty('code')) {
            if (!isNaN(this.selectedPromoCode.code)) {
              let c = this.selectedPromoCode.code.match(/.{1,4}/g);
              this.selectedPromoCode.name = 'Gift card (' + c.join(' ') + ')';
            } else {
              this.selectedPromoCode.name = 'Gift card (' + this.selectedPromoCode.code + ')';
            }
            if (this.subtotal >= this.selectedPromoCode.remaining) {
              this.selectedPromoCode.value_amount = this.selectedPromoCode.remaining;
            } else {
              this.selectedPromoCode.value_amount = this.subtotal;
            }
          }

          if (this.selectedPromoCode.value_amount > this.total && this.selectedPromoCode.redemption_type === 1) {
            this.selectedPromoCode.value_amount = this.total;
          }

          this.promos.push(this.selectedPromoCode);
          this.recalculateTotal();
        }
      }
      this.promoCode = '';
      this.selectedPromoCode = '';
    },

    removePromoCode(item) {
      this.promos = this.promos.filter(p => p.id !== item.id);
      this.recalculateTotal();
    },

    // Drop any applied promo whose quantity rule the current booking no longer
    // satisfies (e.g. participants changed after the promo was applied). Gift
    // cards carry a `code` and have no eligibility rules, so they are kept.
    revalidateAppliedPromos() {
      if (!this.promos || this.promos.length === 0) return;

      const cart = { participants: this.numberOfPlayers, lineItems: 1, uniqueItems: 1 };
      const kept = [];
      const removed = [];

      for (const promo of this.promos) {
        if (promo.hasOwnProperty('code')) {
          kept.push(promo);
          continue;
        }
        const eligibility = PromoRulesService.evaluateCartEligibility(promo, cart);
        if (eligibility.valid) {
          kept.push(promo);
        } else {
          removed.push({ name: promo.name, message: eligibility.error_message });
        }
      }

      if (removed.length > 0) {
        this.promos = kept;
        this.recalculateTotal();
        const names = removed.map(r => r.name).join(', ');
        swal('Promo code removed', `${names} no longer meets the requirements: ${removed[0].message}`, 'warning');
      }
    },

    // ============================================
    // Customer
    // ============================================
    async querySearchCustomer(queryString, cb) {
      if (!queryString || queryString.length < 2) { cb([]); return; }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers?count=10000000&offset=0&first_name=&last_name=&email=&phone=&game=&tag=&autocomplete=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        cb(response.data.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        cb([]);
      }
    },

    // OTC-482: seed the customer from the pinned form request, the same way
    // picking a lookup result does. Duration is left to the slot defaults.
    applyAssigningRequest() {
      const r = this.assigningRequest;
      if (!r) return;
      const first = r.first_name || '';
      const last = r.last_name || '';
      this.user.first_name = first;
      this.user.last_name = last;
      this.user.email = r.customer_email || '';
      this.user.phone = r.customer_phone || '';
      this.user.end_user_id = r.end_user_id || null;
      this.lookupValue = `${first} ${last}`;
      this.sendPaymentRequest = true;
      if (r.end_user_id) {
        this.loadCustomerCredit(r.end_user_id);
      }
      if (r.party_size && this.pricingCategories.length === 1) {
        this.pricingCategories[0].number_of_players = Number(r.party_size);
        this.recalculateTotal();
      }
    },

    handleSelectCustomer(item) {
      this.user.first_name = item.first_name;
      this.user.last_name = item.last_name;
      this.user.email = item.email;
      this.user.phone = item.phone || '';
      this.user.end_user_id = item.end_user_id;
      this.lookupValue = `${item.first_name} ${item.last_name}`;

      this.loadCustomerCredit(item.end_user_id);

      if (this.paymentSettings.payment_gateway_id === 3 && item.end_user_id) {
        this.getStripeCustomer(item.end_user_id);
      }
    },

    handlePhoneUpdate(phoneData) {
      if (phoneData) {
        this.user.phoneE164 = phoneData.e164;
      }
    },

    clearCustomer() {
      this.lookupValue = '';
      this.user = { first_name: '', last_name: '', email: '', phone: '', phoneE164: '', end_user_id: null };
      this.availableCredit = 0;
      this.resetStripePaymentMethods();
    },

    async loadCustomerCredit(customerId) {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers/${customerId}/customer-credit`;
        const response = await axios.get(url, config);
        const credits = response.data.customer_credits || [];
        this.availableCredit = credits.reduce((sum, c) => sum + parseFloat(c.remaining || 0), 0);
      } catch (error) {
        console.error('Error loading customer credit:', error);
        this.availableCredit = 0;
      }
    },

    // ============================================
    // Payment
    // ============================================
    async loadPaymentOptions() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payment-methods`;
        const response = await axios.get(url, config);
        this.paymentOptions = response.data.payment_methods || [];

        if (this.paymentOptions.length > 0) {
          this.selectedPaymentOption = this.paymentOptions[0];
        }
      } catch (error) {
        console.error('Error loading payment options:', error);
        // Fallback payment options
        this.paymentOptions = [
          { id: 1, name: 'Cash', content: '', is_default: false },
          { id: 2, name: 'POS payment', content: '', is_default: false }
        ];
        this.selectedPaymentOption = this.paymentOptions[0];
      }
    },

    onPaymentToggle() {
      if (this.addPayment) {
        // Default mode mirrors the customer-facing booking site: when the
        // slot has any applicable deposit (allowed or required), prefill the
        // deposit; otherwise full. Cleared user-set flag so a late-arriving
        // resolvedPricing prop can flip the default without overriding.
        this.paymentModeUserSet = false;
        this.paymentMode = this.depositApplicable ? 'deposit' : 'full';
        this.partialAmount = this.paymentMode === 'deposit' ? this.depositChargeAmount : this.total;
        this.errs.partial_amount = '';
        this.initializePaymentForm();
      }
    },

    onPaymentModeChange(mode) {
      this.paymentMode = mode;
      this.paymentModeUserSet = true;
      if (mode === 'deposit') {
        this.partialAmount = this.depositChargeAmount;
      } else if (mode === 'full') {
        this.partialAmount = this.total;
      }
      // 'custom' leaves the current value alone so staff can keep typing.
      this.validatePartialAmount();
    },

    validatePartialAmount() {
      const amount = this.partialAmount;
      if (amount === null || amount === undefined || amount === '') {
        this.errs.partial_amount = '';
        return;
      }

      const stripeMinimum = 0.50;
      const squareMinimum = 1.00;

      if (isNaN(amount)) {
        this.errs.partial_amount = 'Please enter a valid amount';
      } else if (amount <= 0) {
        this.errs.partial_amount = 'Amount must be greater than zero';
      } else if (!this.canExceedTotal && amount > this.total) {
        this.errs.partial_amount = 'Amount cannot exceed total';
      } else if (this.selectedPaymentOption.name === 'Card' && this.paymentSettings.payment_gateway_id === 3 && amount < stripeMinimum) {
        this.errs.partial_amount = `Minimum card payment is ${this.getAmount(stripeMinimum)}`;
      } else if (this.selectedPaymentOption.name === 'Card' && this.paymentSettings.payment_gateway_id === 2 && amount < squareMinimum) {
        this.errs.partial_amount = `Minimum card payment is ${this.getAmount(squareMinimum)}`;
      } else if (this.selectedPaymentOption.name === 'Customer credit' && toCents(amount) > toCents(this.availableCredit)) {
        this.errs.partial_amount = 'Amount cannot exceed available credit';
      } else if (this.selectedPaymentOption.name === 'Gift card' && this.selectedGiftCard && toCents(amount) > toCents(this.selectedGiftCard.remaining)) {
        this.errs.partial_amount = 'Amount cannot exceed gift card balance';
      } else {
        this.errs.partial_amount = '';
      }
    },

    async initializePaymentForm() {
      if (this.paymentSettings.payment_gateway_id === 3 && this.selectedPaymentOption.name === 'Card') {
        await this.$nextTick();
        await this.initializeStripe();
      }
      if (this.paymentSettings.payment_gateway_id === 2 && this.selectedPaymentOption.name === 'Card') {
        await this.$nextTick();
        await this.initializeSquare();
      }
      if (this.paymentSettings.payment_gateway_id === 4 && this.selectedPaymentOption.name === 'Card') {
        await this.loadPaystack();
      }
    },

    // Stripe
    loadStripe() {
      return new Promise((resolve) => {
        const stripeExists = document.getElementById('stripev3');
        if (stripeExists) {
          if (window.Stripe) resolve();
          else stripeExists.addEventListener('load', resolve);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.id = 'stripev3';
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },

    async initializeStripe() {
      if (this.paymentSettings.payment_gateway_id !== 3) return;
      await this.loadStripe();
      await this.$nextTick();

      const cardElement = document.getElementById('card-element-drawer-booking');
      if (!cardElement) return;
      if (this.card && cardElement.children.length > 0) return;

      // A Stripe Connect venue tokenizes inside its connected account (platform key + account).
      const accountOpts = this.paymentSettings.stripe_account_id ? { stripeAccount: this.paymentSettings.stripe_account_id } : undefined;
      this.stripe = window.Stripe(this.paymentSettings.api_name, accountOpts);
      this.elements = this.stripe.elements();
      const isLightMode = document.body.classList.contains('white-content');
      const style = {
        base: {
          fontSize: '16px',
          fontFamily: 'Poppins, sans-serif',
          color: isLightMode ? '#344675' : '#ffffff'
        }
      };
      this.card = this.elements.create('card', { style });
      this.card.mount('#card-element-drawer-booking');
    },

    async getStripeCustomer(endUserId) {
      if (!endUserId || this.paymentSettings.payment_gateway_id !== 3) return;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers/${endUserId}/stripe-customer`;
        const response = await axios.get(url, config);
        if (response.data.stripe_customer) {
          this.stripeCustomer = response.data.stripe_customer;
          this.stripePaymentMethods = response.data.payment_methods || [];
          if (this.stripePaymentMethods.length > 0) {
            const defaultCard = this.stripePaymentMethods.find(pm => pm.is_default);
            this.selectedPaymentMethod = defaultCard ? defaultCard.id : this.stripePaymentMethods[0].id;
            this.selectedStoredCard = defaultCard || this.stripePaymentMethods[0];
          } else {
            this.selectedPaymentMethod = 'new';
            this.selectedStoredCard = null;
          }
        }
      } catch (err) {
        this.stripeCustomer = null;
        this.stripePaymentMethods = [];
        this.selectedPaymentMethod = 'new';
        this.selectedStoredCard = null;
      }
    },

    async onPaymentMethodChange() {
      if (this.selectedPaymentMethod === 'new') {
        this.selectedStoredCard = null;
        if (this.card) { this.card.unmount(); this.card = null; }
        await this.$nextTick();
        await this.initializeStripe();
      } else {
        this.selectedStoredCard = this.stripePaymentMethods.find(pm => pm.id === this.selectedPaymentMethod);
      }
    },

    resetStripePaymentMethods() {
      this.selectedPaymentMethod = 'new';
      this.selectedStoredCard = null;
      this.saveCardForFuture = false;
      this.stripeCustomer = null;
      this.stripePaymentMethods = [];
    },

    // Square
    loadSquare(isLive) {
      return new Promise((resolve) => {
        if (window.Square) { resolve(); return; }
        const script = document.createElement('script');
        script.src = isLive
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },

    async initializeSquare() {
      if (this.paymentSettings.payment_gateway_id !== 2) return;
      await this.loadSquare(this.paymentSettings.is_live);
      await this.$nextTick();
      if (!window.Square) return;

      const cardContainer = document.getElementById('card-container-drawer-booking');
      if (!cardContainer) return;

      if (this.squareCard) {
        try { await this.squareCard.destroy(); } catch (e) { /* ignore */ }
        this.squareCard = null;
      }

      this.square = window.Square.payments(this.paymentSettings.api_name, this.paymentSettings.api_location);

      const isLightMode = document.body.classList.contains('white-content');
      const customStyle = {
        '.input-container.is-focus': { borderColor: '#e14eca' },
        '.input-container.is-error': { borderColor: '#ff1600' },
        '.message-text.is-error': { color: '#ff1600' },
        '.message-icon.is-error': { color: '#ff1600' },
        'input.is-error': { color: '#ff1600' },
        '.input-container': {
          borderRadius: '6px',
          borderColor: isLightMode ? '#e3e3e3' : 'rgba(255, 255, 255, 0.2)',
        },
        '.message-text': { color: isLightMode ? '#9A9A9A' : '#adb5bd' },
        input: {
          backgroundColor: isLightMode ? '#ffffff' : '#22233a',
          color: isLightMode ? '#344675' : '#ffffff',
        },
        'input::placeholder': { color: isLightMode ? '#9A9A9A' : '#adb5bd' },
      };

      try {
        this.squareCard = await this.square.card({ style: customStyle });
        await this.squareCard.attach('#card-container-drawer-booking');
      } catch (e) {
        console.error('Initializing Square card failed', e);
      }
    },

    async tokenize(paymentMethod) {
      const tokenResult = await paymentMethod.tokenize();
      if (tokenResult.status === 'OK') {
        const zip = tokenResult.details.billing.postalCode ? tokenResult.details.billing.postalCode : ''
        return { token: tokenResult.token, zip }
      } else {
        let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
        if (tokenResult.errors) {
          errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
        }
        throw new Error(errorMessage);
      }
    },

    async verifyBuyer(payments, token, zip) {
      const verificationDetails = {
        amount: this.total.toString(),
        billingContact: {
          familyName: this.user.last_name,
          givenName: this.user.first_name,
          postalCode: zip
        },
        currencyCode: this.currency,
        intent: 'CHARGE',
      };
      const verificationResults = await payments.verifyBuyer(token, verificationDetails);
      return verificationResults.token;
    },

    // Paystack
    loadPaystack() {
      return new Promise((resolve) => {
        if (window.PaystackPop) { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v2/inline.js';
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },

    // Gift card
    async giftCardSearch(queryString, cb) {
      if (!queryString || queryString.length < 2) { cb([]); return; }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/gifts/autocomplete?q=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        const suggestions = [];
        const giftCards = response.data.gift_cards || [];
        for (let n in giftCards) {
          if (giftCards[n].code != null) {
            suggestions.push({
              value: JSON.stringify(giftCards[n]),
              id: giftCards[n].id,
              code: giftCards[n].code,
              customer_id: giftCards[n].customer_id,
              date_purchased: giftCards[n].date_purchased,
              email: giftCards[n].email,
              first_name: giftCards[n].first_name,
              last_name: giftCards[n].last_name,
              remaining: giftCards[n].remaining,
              total: giftCards[n].total
            });
          }
        }
        cb(suggestions);
      } catch (error) {
        console.error('Error searching gift cards:', error);
        cb([]);
      }
    },

    handleGiftCardSelect(item) {
      this.selectedGiftCard = item;
      this.giftCardCode = this.getGiftCardCode(item.code);
    },

    getCardBrandIcon(brand) {
      const icons = {
        'visa': 'fab fa-cc-visa', 'mastercard': 'fab fa-cc-mastercard',
        'amex': 'fab fa-cc-amex', 'discover': 'fab fa-cc-discover',
        'diners': 'fab fa-cc-diners-club', 'jcb': 'fab fa-cc-jcb'
      };
      return icons[brand?.toLowerCase()] || 'far fa-credit-card';
    },

    getCardBrandName(brand) {
      const names = {
        'visa': 'Visa', 'mastercard': 'Mastercard', 'amex': 'American Express',
        'discover': 'Discover', 'diners': 'Diners Club', 'jcb': 'JCB'
      };
      return names[brand?.toLowerCase()] || brand || 'Card';
    },

    // ============================================
    // Validation & Submission
    // ============================================
    validateForm() {
      let valid = true;
      this.errs = { first_name: '', last_name: '', email: '', phone: '', partial_amount: '', gift_card_code: '' };

      if (!this.booking) {
        swal('Error', 'Please select a booking slot', 'error');
        return false;
      }

      // Append mode skips customer + phone validation — the customer is
      // already pinned to the parent transaction and isn't editable here.
      if (!this.isAppendMode) {
        if (!this.user.first_name) {
          this.errs.first_name = 'First name is required';
          valid = false;
        }
        if (!this.user.last_name) {
          this.errs.last_name = 'Last name is required';
          valid = false;
        }
        if (!this.user.email) {
          this.errs.email = 'Email is required';
          valid = false;
        } else if (!validateEmail(this.user.email)) {
          this.errs.email = 'Please enter a valid email';
          valid = false;
        }
        if (!validateInternalCustomerPhone(this.user, JSON.parse(localStorage.getItem('group') || '{}'))) {
          this.errs.phone = 'Phone number is required';
          valid = false;
        }
      }
      if (this.numberOfPlayers <= 0) {
        swal('Error', 'Please select at least one player', 'error');
        valid = false;
      }

      for (const line of this.stagedItems) {
        const qty = parseInt(line.quantity, 10) || 0;
        const overStock = line.id && !line.unlimited_available_quantity && qty > Number(line.available);
        if (qty < 1 || overStock) {
          this.$notify({ type: 'warning', message: `Check the quantity on ${line.name}`, verticalAlign: 'top', horizontalAlign: 'right', timeout: 4000 });
          valid = false;
          break;
        }
      }

      if (this.visibleCustomFields.length > 0 && this.$refs.cfRenderer && !this.$refs.cfRenderer.validate()) {
        valid = false;
      }

      if (this.addPayment) {
        if (!this.canExceedTotal && this.partialAmount > this.total) {
          this.errs.partial_amount = 'Amount cannot exceed total';
          valid = false;
        }
        if (this.selectedPaymentOption.name === 'Gift card') {
          if (!this.selectedGiftCard) {
            this.errs.gift_card_code = 'Please select a valid gift card';
            valid = false;
          } else if (toCents(this.partialAmount) > toCents(this.selectedGiftCard.remaining)) {
            this.errs.partial_amount = 'Amount exceeds gift card balance';
            valid = false;
          }
        }
        if (this.selectedPaymentOption.name === 'Customer credit') {
          if (toCents(this.partialAmount) > toCents(this.availableCredit)) {
            this.errs.partial_amount = 'Amount cannot exceed available credit';
            valid = false;
          }
        }
      }

      return valid;
    },

    async createBooking() {
      // Cart-mode preflight. Validate that we have at least one cart line
      // with players before letting the existing payload-build path run.
      // Each cart entry needs a matching cartLineState emission with at
      // least one ticket > 0 — bail with a clear toast otherwise so the
      // backend doesn't reject a "0 players" booking.
      if (this.isCartMode) {
        if (this.cartSlots.length === 0) {
          this.$notify({ type: 'warning', message: 'Add at least one booking to the cart before checking out.', verticalAlign: 'top', horizontalAlign: 'right', timeout: 4000 });
          return;
        }
        const emptyLines = this.cartSlots.filter(s => {
          const line = this.cartLineState[String(s.id)];
          return !line || !line.numberOfPlayers || line.numberOfPlayers <= 0;
        });
        if (emptyLines.length > 0) {
          const names = emptyLines.map(s => s.game_name).join(', ');
          this.$notify({ type: 'warning', message: `Add at least one ticket to: ${names}`, verticalAlign: 'top', horizontalAlign: 'right', timeout: 5000 });
          return;
        }
      }
      if (!this.validateForm()) return;
      if (this.isAppendMode) return this.appendBookingToTransaction();
      this.loading = true;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions`;

        const slot = this.booking;
        const rawData = slot.originalData || slot;
        const bookingDate = this.extractDate(rawData.booking_date);
        const endDate = this.extractDate(rawData.end_date || rawData.booking_date);

        const data = {
          bookings: [{
            id: slot.id || rawData.id,
            group_size: this.numberOfPlayers,
            game_id: slot.gameId || slot.game_id || rawData.game_id,
            game_name: slot.gameName || slot.game_name || rawData.game_name,
            booking_date: bookingDate,
            end_date: endDate,
            start_time: slot.startTimeRaw || slot.start_time || rawData.start_time,
            end_time: slot.endTimeRaw || slot.end_time || rawData.end_time,
            selected_duration_id: this.selectedDurationId,
            total: this.bookingSubtotal,
            tickets: this.pricingCategories,
            // Tells the backend to book the entire slot (private upgrade) and
            // use reservation_upgrade_fee instead of summing tickets.
            book_entire_slot: this.upgradeToPrivate
          }],
          send_payment_request: this.sendPaymentRequest,
          booking_request_id: this.assigningRequest ? this.assigningRequest.id : undefined,
          subtotal: this.subtotal,
          total: this.total,
          tax: this.waiveTax ? 0 : roundCents(this.totalTax + this.inclusiveTax),
          discount: this.shelfDiscount,
          items: this.itemsPayload(),
          no_confirmation_email: this.noConfirmationEmail,
          promo_codes: this.promos,
          fee: this.waiveFee ? 0 : this.totalFee,
          waive_taxes: this.waiveTax ? 1 : 0,
          waive_fees: this.waiveFee ? 1 : 0,
          user: {
            ...this.user,
            phone: this.user.phoneE164 || this.user.phone
          },
          custom_field_responses: this.customFieldResponsesPayload()
        };

        // Cart mode: replace the single-slot bookings array with one
        // entry per cart slot. The lambda (create-transaction-for-group-v2)
        // already loops `b.bookings` and creates tickets + handles public/
        // private routing per slot, so this is the only payload change
        // needed beyond the aggregate subtotal/total which recalculateTotal
        // already sums into this.subtotal / this.total via cartLineState.
        if (this.isCartMode) {
          data.bookings = this.cartSlots.map(s => {
            const line = this.cartLineState[String(s.id)] || {};
            return {
              id: s.id,
              game_id: s.game_id,
              game_name: s.game_name,
              booking_date: this.extractDate(s.booking_date),
              end_date: this.extractDate(s.end_date || s.booking_date),
              start_time: s.start_time,
              end_time: s.end_time,
              group_size: line.numberOfPlayers || 0,
              total: line.subtotal || 0,
              tickets: line.tickets || [],
              selected_duration_id: null,
              book_entire_slot: line.upgradeToPrivate || false
            };
          });
        }

        // Handle payment
        if (!this.addPayment) {
          data.partial_amount = 0;
        } else {
          data.partial_amount = this.partialAmount;

          if (this.selectedPaymentOption.name === 'Cash') {
            data.payment_type = 'cash';
          } else if (this.selectedPaymentOption.name === 'POS payment') {
            data.payment_type = 'pos';
          } else if (!this.selectedPaymentOption.is_default) {
            data.payment_type = this.selectedPaymentOption.name;
            data.custom_payment_type = true;
          } else if (this.selectedPaymentOption.name === 'Card') {
            if (this.paymentSettings.payment_gateway_id === 3) {
              // Stripe — matches NewTransaction.vue's handleStripePayment flow
              data.payment = this.payment;
              const isStoredCard = this.selectedPaymentMethod !== 'new' && this.selectedStoredCard;
              if (isStoredCard) {
                data.payment_method_id = this.selectedStoredCard.payment_method_id;
                data.stripe_customer_id = this.stripeCustomer?.stripe_customer_id;
              } else {
                const { token: stripeToken, error } = await this.stripe.createToken(this.card);
                if (error) {
                  swal('Error', 'There was an issue with your payment', 'error');
                  this.loading = false;
                  return;
                }
                data.token = stripeToken.id;
                if (this.saveCardForFuture && (this.user.end_user_id || this.user.email)) {
                  data.save_card = true;
                  if (this.user.end_user_id) {
                    data.customer_id = this.user.end_user_id;
                  }
                }
              }
            } else if (this.paymentSettings.payment_gateway_id === 2) {
              // Square — matches NewTransaction.vue's createSquarePayment flow
              if (!this.squareCard) {
                swal('Error', 'Payment form not loaded. Please try again.', 'error');
                this.loading = false;
                return;
              }
              data.locationId = this.paymentSettings.api_location;
              data.payment = this.payment;
              try {
                const token = await this.tokenize(this.squareCard);
                const verificationToken = await this.verifyBuyer(this.square, token.token, token.zip);
                data.sourceId = token.token;
                data.verificationToken = verificationToken;
                data.payment.zip = token.zip;
                if (this.saveCardForFuture && (this.user.end_user_id || this.user.email)) {
                  const token2 = await this.tokenize(this.squareCard);
                  data.sourceId2 = token2.token;
                  data.save_card = true;
                  if (this.user.end_user_id) {
                    data.customer_id = this.user.end_user_id;
                  }
                }
              } catch (err) {
                console.error('Square tokenize/verify failed', err);
                swal('Error', err?.message || 'There was an issue with your payment', 'error');
                this.loading = false;
                return;
              }
            } else if (this.paymentSettings.payment_gateway_id === 1) {
              // Authorize.net
              try {
                this.authorize_opaque_data = await this.$refs.authorizePaymentForm.tokenize();
              } catch (err) {
                this.$notify({ type: 'danger', message: err.message });
                this.loading = false;
                return;
              }
              data.payment = { opaqueData: this.authorize_opaque_data, zip: this.$refs.authorizePaymentForm.zip };
            } else if (this.paymentSettings.payment_gateway_id === 4) {
              // Paystack
              this.loading = false;
              await this.payWithPaystack();
              return;
            }
          } else if (this.selectedPaymentOption.name === 'Customer credit') {
            data.customer_credit = this.partialAmount;
          } else if (this.selectedPaymentOption.name === 'Gift card') {
            data.applied_gift_card = this.selectedGiftCard;
            data.payment_type = 'gift_card';
          }
        }

        await axios.put(url, data, config);

        const successMessage = this.isCartMode
          ? `${this.cartSlots.length} ${this.cartSlots.length === 1 ? 'booking' : 'bookings'} created`
          : 'Booking created successfully';
        this.$notify({
          type: 'success',
          message: successMessage,
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });

        // Cart mode: clear cart state + exit cart mode before the drawer
        // closes via the parent's @created handler. Without this, the
        // toolbar would still show the cart count and the next click
        // would land back in cart mode unexpectedly.
        if (this.isCartMode) {
          this.cartLineState = {};
          exitMultiSelect();
        }
        this.stagedItems = [];

        this.$emit('created');
        if (this.assigningRequest) this.$emit('request-approved', this.assigningRequest.id);
      } catch (error) {
        console.error('Error creating booking:', error);
        if ([400, 409, 500].includes(error.response?.status)) {
          swal('Error', error.response.data || 'Failed to create booking', 'error');
        } else {
          swal('Error', 'Failed to create booking', 'error');
        }
      } finally {
        this.loading = false;
      }
    },

    async appendBookingToTransaction() {
      const transactionId = this.appendTargetTransactionId;
      if (!transactionId) {
        swal('Error', 'Append session is missing a transaction id', 'error');
        return;
      }
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${transactionId}/bookings`;

        const slot = this.booking;
        const rawData = slot.originalData || slot;
        const data = {
          booking_slot_id: slot.id || rawData.id,
          selected_duration_id: this.selectedDurationId,
          number_of_players: this.numberOfPlayers,
          price: this.subtotal,
          tickets: this.pricingCategories,
          // Upgrade-to-private is supported when appending a new slot. price
          // already includes the private fee (applyPrivateUpgrade); this flag
          // tells the backend to stamp the slot as upgraded + snapshot the mode.
          book_entire_slot: this.upgradeToPrivate,
          send_confirmation_email: !this.noConfirmationEmail
        };

        const response = await axios.put(url, data, config);

        // Lambda returns the post-recompute totals { paid, due, total } so the
        // append banner stays accurate (subtotal alone misses taxes/fees the
        // server allocates to the new booking). Guard for older lambda
        // versions that returned 204 with no body.
        const authoritativeDue = response?.data?.due;
        if (authoritativeDue !== undefined && authoritativeDue !== null) {
          updateAppendModeBalance(authoritativeDue);
        }

        this.$notify({
          type: 'success',
          message: 'Booking added to transaction',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });

        this.$emit('created');
        if (this.assigningRequest) this.$emit('request-approved', this.assigningRequest.id);
      } catch (error) {
        console.error('Error appending booking:', error);
        const status = error.response?.status;
        // 401 = lost auth, 404/410 = transaction is gone (deleted, refunded
        // to nothing, etc.). In all three the append session is meaningless,
        // so clear it before the user clicks another slot into the same dead
        // banner.
        if (status === 401) {
          exitAppendMode();
          swal('Error', 'Not authorized to add bookings to this transaction', 'error');
        } else if (status === 404 || status === 410) {
          exitAppendMode();
          swal('Error', 'This transaction is no longer available. Append mode has been cancelled.', 'error');
        } else {
          swal('Error', error.response?.data || 'Failed to add booking', 'error');
        }
      } finally {
        this.loading = false;
      }
    },

    async payWithPaystack() {
      await this.loadPaystack();

      const amount = Math.round(Number(parseFloat(this.partialAmount) + 'e2'));

      // Metadata lets the paystack-webhook orphan-log this charge with full
      // recovery context if the callback below never reaches the lambda (the
      // cart exists only in this browser). A session hiccup must never block
      // a charge, so the operator email is best-effort.
      let operatorEmail = null;
      try {
        const session = await Auth.currentSession();
        operatorEmail = session.getIdToken().payload.email;
      } catch (e) {
        operatorEmail = null;
      }
      const popup = new window.PaystackPop();
      popup.newTransaction({
        key: this.paymentSettings.api_name,
        email: this.user.email,
        amount: amount,
        reference: '' + Math.floor((Math.random() * 1000000000) + 1),
        metadata: {
          otc: 1,
          otc_flow: 'console_new_transaction',
          otc_company_group_id: this.companyGroupId,
          otc_company_id: this.companyId,
          ...(operatorEmail ? { otc_user_email: operatorEmail } : {})
        },
        onSuccess: (response) => {
          // The lambda reads paystack_reference.transaction (v1 field name);
          // v2 carries the numeric transaction id as response.trans (verified from a live callback).
          this.handlePaystackPayment({ ...response, transaction: response.trans });
        }
      });
    },

    async handlePaystackPayment(reference) {
      this.loading = true;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions`;

        const slot = this.booking;
        const rawData = slot.originalData || slot;
        const bookingDate = this.extractDate(rawData.booking_date);
        const endDate = this.extractDate(rawData.end_date || rawData.booking_date);

        const data = {
          bookings: [{
            id: slot.id || rawData.id,
            group_size: this.numberOfPlayers,
            game_id: slot.gameId || slot.game_id || rawData.game_id,
            game_name: slot.gameName || slot.game_name || rawData.game_name,
            booking_date: bookingDate,
            end_date: endDate,
            start_time: slot.startTimeRaw || slot.start_time || rawData.start_time,
            end_time: slot.endTimeRaw || slot.end_time || rawData.end_time,
            selected_duration_id: this.selectedDurationId,
            total: this.bookingSubtotal,
            tickets: this.pricingCategories,
            // Tells the backend to book the entire slot (private upgrade) and
            // use reservation_upgrade_fee instead of summing tickets.
            book_entire_slot: this.upgradeToPrivate
          }],
          send_payment_request: this.sendPaymentRequest,
          booking_request_id: this.assigningRequest ? this.assigningRequest.id : undefined,
          subtotal: this.subtotal,
          total: this.total,
          tax: this.waiveTax ? 0 : roundCents(this.totalTax + this.inclusiveTax),
          discount: this.shelfDiscount,
          items: this.itemsPayload(),
          no_confirmation_email: this.noConfirmationEmail,
          promo_codes: this.promos,
          fee: this.waiveFee ? 0 : this.totalFee,
          waive_taxes: this.waiveTax ? 1 : 0,
          waive_fees: this.waiveFee ? 1 : 0,
          user: {
            ...this.user,
            phone: this.user.phoneE164 || this.user.phone
          },
          custom_field_responses: this.customFieldResponsesPayload(),
          paystack_reference: reference,
          payment: { cardNumber: '1111' },
          partial_amount: this.partialAmount
        };

        await axios.put(url, data, config);

        this.$notify({
          type: 'success',
          message: 'Booking created successfully',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
        this.stagedItems = [];

        this.$emit('created');
        if (this.assigningRequest) this.$emit('request-approved', this.assigningRequest.id);
      } catch (error) {
        console.error('Error creating booking:', error);
        if ([400, 409, 500].includes(error.response?.status)) {
          swal('Error', error.response.data || 'Failed to create booking', 'error');
        } else {
          swal('Error', 'Failed to create booking', 'error');
        }
      } finally {
        this.loading = false;
      }
    },

    // ============================================
    // Formatting helpers
    // ============================================
    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currency_format,
        numberFormat: this.number_format
      });
    },

    extractDate(d) {
      if (!d) return '';
      if (d.length > 10 && d.includes('T')) return d.split('T')[0];
      if (d.includes('-') && d.length >= 10) return d.substring(0, 10);
      return d;
    },

    getGiftCardCode(code) {
      if (!code) return '';
      return code.substring(0, 4) + '****' + code.substring(code.length - 4);
    }
  }
};
</script>

<style lang="scss" scoped>
.create-booking-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// The item picker replaces the form body while open and owns the scroll.
.create-booking-view__picker {
  flex: 1;
  min-height: 0;
}

.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.back-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(255, 255, 255, 0.1); color: white; }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

// Stacks title + append-mode subtitle. Flex column so subtitle sits directly
// below the title without affecting the back-button's vertical alignment.
.view-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.view-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.view-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

// Sections
.form-section {
  margin-bottom: 16px;
}

.form-section--inline {
  display: flex;
  align-items: center;
}

.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.field-label {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
}

.section-divider {
  border-top: 1px solid #e14eca;
  margin: 16px 0;
}

// Form layout
.form-field {
  margin-bottom: 12px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-field--half {
  flex: 1;
  min-width: 0;
}

// Narrower field used by the payment amount input — the field reads more
// naturally as a focused input than as a full-width row, especially since
// it's paired with a segmented toggle above it.
.form-field--narrow {
  flex: 0 0 220px;
  max-width: 220px;
  min-width: 0;
}

// Upgrade-to-private toggle (above Tickets list)
.upgrade-toggle {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: rgba(225, 78, 202, 0.08);
  border-left: 3px solid #e14eca;
  border-radius: 6px;
}

.upgrade-toggle__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upgrade-toggle__label {
  font-size: 13px;
  margin-bottom: 0;
}

.upgrade-toggle__desc {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.7);

  strong { color: #e14eca; font-weight: 600; }
}

// Pricing rows
.pricing-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

// The receipt opens with its own dashed divider (same as the booked slot
// drawer), so the last ticket row drops its solid border to avoid a double line.
.pricing-row:has(+ .receipt) {
  border-bottom: 0;
}

.pricing-row__info {
  flex: 1;
  min-width: 0;
}

.pricing-row__name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pricing-row__name {
  font-size: 13px;
  font-weight: 500;
}

.pricing-row__rate {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  display: block;
  margin-top: 2px;
}

.pricing-row__edit-icon {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  &:hover { color: #e14eca; }
}

.pricing-row__inline-edit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.pricing-row__price-input {
  width: 90px;
}

.pricing-row__save-icon {
  color: #2dce89;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #24a46d; }
}

.pricing-row__restore-icon {
  color: #1d8cf8;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #1171d6; }
}

.pricing-row__cancel-icon {
  color: #f5365c;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #d42a4a; }
}

.pricing-row__qty {
  width: 110px;
}

.pricing-row__total {
  width: 80px;
  text-align: right;
  font-weight: 500;
  font-size: 13px;
  padding-top: 22px;
}

// Summary lines
.summary-line {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.summary-line--total {
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 14px;
}

.remove-promo {
  background: none;
  border: none;
  color: #f5365c;
  cursor: pointer;
  padding: 0 4px;
  font-size: 10px;
}

// Promo input
.apply-promo-append {
  background: transparent;
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 10px;
  white-space: nowrap;
  &:hover { color: rgba(255, 255, 255, 0.7); }
}

.lookup-clear-append {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0 8px;
  font-size: 14px;
  &:hover { color: #e14eca; }
}

// Email / switch option rows
.email-option-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

// Breathing room between the "Take payment now" switch and the deposit
// info-hint so the callout doesn't crowd against the toggle row.
.payment-deposit-hint {
  margin-top: 16px;
}

.payment-mode-row {
  margin: 12px 0 8px;
}

// Payment amount
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);

  &:focus-within { border-color: #e14eca; }
  &.has-error { border-color: #ec250d; }
  &.is-locked {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(30, 30, 47, 0.4);
  }
  &.is-locked .amount-prefix {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
  }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;
}

.amount-input-wrapper ::v-deep .form-control {
  border: none;
  border-radius: 0;
  background: transparent;
}

.amount-input-wrapper ::v-deep .el-input-number {
  width: 100%;
}

.amount-input-wrapper ::v-deep .el-input-number .el-input__inner {
  text-align: left;
  color: white;
  background: transparent;
  border: none;
}

.amount-input-group.available-credit {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(30, 30, 47, 0.4);
  opacity: 0.8;
}

.amount-input-group.available-credit .amount-prefix {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

.amount-input-group.available-credit ::v-deep .el-input-number .el-input__inner {
  color: rgba(255, 255, 255, 0.7);
}

// Stored card
.stored-card-display {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 12px;
}

.stored-card-icon {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.6);
}

// Footer (matches EditSlotView's .view-footer pattern — full-width padding,
// thin top-border separator, no card treatment).
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  margin: 16px -24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

// Waive-tax/fees row, folded into the Tickets section
.waive-row {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
}

</style>

<style lang="scss">
/* Belt-and-suspenders override for the raw <label> that base-input renders.
   The same rule lives in BaseInput.vue's <style> block, but if HMR/build
   caching delays it, this scoped fallback keeps CreateBookingView consistent. */
.create-booking-view .form-group.has-label > label,
.create-booking-view .form-group > label {
  display: block !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  margin-bottom: 6px !important;
}

/* DrawerSection (the global component) handles its own light-mode styling.
   Just give the body a soft tint so the white sections read as cards. */
.white-content .create-booking-view .view-body {
  background: #f5f6fa;
}
.white-content .create-booking-view .waive-row {
  border-top-color: #e9ecef;
}

.white-content {
  .create-booking-view .view-header {
    border-bottom-color: #e3e3e3;
  }
  .create-booking-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;
    &:hover { background: #e9ecef; color: #344675; }
  }
  .create-booking-view .view-title {
    color: #344675;
  }
  .create-booking-view .view-subtitle {
    color: rgba(52, 70, 117, 0.65);
  }
  .create-booking-view .section-label {
    color: #525f7f;
  }
  .create-booking-view .field-label {
    color: #525f7f;
  }
  .create-booking-view .form-group > label,
  .create-booking-view .form-group.has-label > label {
    color: #525f7f !important;
  }
  .create-booking-view .section-divider {
    border-color: #e14eca;
  }
  .create-booking-view .upgrade-toggle__desc {
    color: rgba(52, 70, 117, 0.75);
  }
  .create-booking-view .pricing-row {
    border-bottom-color: #e3e3e3;
  }
  .create-booking-view .pricing-row__rate {
    color: #9A9A9A;
  }
  .create-booking-view .summary-line {
    color: #525f7f;
  }
  .create-booking-view .summary-line--total {
    border-top-color: #e3e3e3;
  }
  .create-booking-view .stored-card-display {
    background: #f5f6fa;
    border-color: #e3e3e3;
  }
  .create-booking-view .form-actions {
    border-top-color: #e3e3e3;
  }
  // Element's .el-input-group__append is magenta ($primary) in both themes,
  // so its buttons keep light text in light mode too (not a dark flip).
  .create-booking-view .lookup-clear-append {
    color: rgba(255, 255, 255, 0.85);
  }
  .create-booking-view .amount-input-group {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }
  .create-booking-view .amount-input-group.is-locked {
    border-color: #e3e3e3;
    background: #f0f0f0;
  }
  .create-booking-view .amount-input-group.is-locked .amount-prefix {
    background: #e9ecef;
    color: #525f7f;
  }
  .create-booking-view .amount-prefix {
    background: #f0f0f0;
    color: #525f7f;
  }
  .create-booking-view .amount-input-wrapper .el-input-number .el-input__inner {
    color: #344675;
  }
  .create-booking-view .amount-input-group.available-credit {
    border-color: #e3e3e3;
    background: #f8f9fa;
  }
  .create-booking-view .amount-input-group.available-credit .amount-prefix {
    background: #f0f0f0;
    color: #9A9A9A;
  }
  .create-booking-view .apply-promo-append {
    color: #ffffff;
  }
  .create-booking-view .text-white {
    color: #344675 !important;
  }
}
</style>
